import PropTypes from "prop-types";
import {
  mAlignCenter,
  formButton,
  formSubmitButton,
} from "@/assets/styles/common.module.scss";
import classNames from "classnames";
import {
  premiumStyle,
  rupeeFontStyle,
  UinBottomLine,
  prodNameBottomLine,
  additionalInfoHeading,
  policyLink,
  policyLinkDivider,
} from "../quote.module.scss";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useMemo,
  useLayoutEffect,
} from "react";
import { useTranslation } from "react-i18next";
import PackageCanvasMenu from "../modals/PackageModal";
import PremiumCompute from "@/service/premium-compute";
import { IMAGE_BASE_PATH } from "@/config/config";
import Image from "next/image";
import { toAmount } from "@/helpers/utils";
import { useDispatch, useSelector } from "react-redux";
import {
  getCustomerType,
  getMetadata,
} from "@/features/metadata/metadataSlice";
import {
  PRODUCT_NAME_UIN_MAP_MEDICARE,
  PRODUCT_NAME_LABEL_MAP_MEDICARE,
  MIGRATION_PRODUCER_ROLES,
  NEW_PRODUCT_NAME_UIN_MAP_MEDICARE,
  PRICE_REVISION_VARIANT,
  PRICE_REVISION_CUTOFF_DATE,
  MEDICARE_SELECT_CODE,
  HEALTH_SUPERCHARGE_CODE,
  GEO_PLAN,
  VALUE_PLAN,
  ROOM_CATEGORY_SINGLE_PRIVATE,
  getCISLink,
  GRIEVANCE_REDRESSAL_LINK,
  MEDICARE_SELECT_REVISION_CODE,
  MEDICARE_SELECT_SUBPRODUCT_UIN_MAP,
  MEDICARE_SELECT_SMART_VARIANT_NAME,
  MEDICARE_SELECT_ELITE_VARIANT_NAME,
  PACKAGES_COVERS_ENABLED_SUBPRODUCT_CODES
} from "@/config/constants";
import { EVENTS } from "@/config/events";
import { invokeMoengageAnalytics } from "@/utils/moengage";

import {
  getMigrationDetails,
  getRenewalDetailsDataSelector,
  getSelectedCard,
  setMigrationDetails,
  setPropSelectedCard,
  setOptedCovers,
  savePreComputedPackagesCovers,
  checkPackagesCoversDirty,
  getPreComputedPackagesCovers,
  getCustomPlanDirty,
  getMigrationPlanDirty,
  getIsApp,
  getProductCode,
  setCalculatedCustomPlan,
  getCalculatedCustomPlan
} from "@/features/quote/quoteSlice";
import ModalBox from "@/components/core/ModalBox";
import { Button } from "@/components/core/form";
import AutoDebitModal from "../modals/AutoDebitModal";
import RedirectModal from "../modals/RedirectModal";
import axiosInstance from "@/middleware/axios-wrapper";
import NeoSelect from "./NeoSelect";

const AddOnsCard = forwardRef(
  (
    {
      setAddOnPremium,
      premiumSchemes,
      variant,
      setAddOnScheme,
      setSelectedPackagesArr,
      addonCovers,
      setShowPremiumBreakupModal,
      setPremiumDataToShow,
      setCustomerRecommendedPlan,
      setCustomerUpsellFlag,
      setRemarkMigrationPolicy,
      medicareBoltRedirection,
      productName,
      productCode,
    },
    ref
  ) => {
    const rupeesToLakhLabel = (rs) => {
      const lakhs = Number(rs) / 100000;
      const normalized = Math.round(lakhs * 100) / 100;
      const str = String(normalized);
      return `${str.replace(/\.0+$/, "")} Lakh`;
    };

    const lakhValueToLabel = (l) => {
      const str = String(l);
      return `${str.replace(/\.0+$/, "")} Lakh`;
    };
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const userType = useSelector(getCustomerType);
    const metadata = useSelector(getMetadata);
    const propSelectedCard = useSelector(getSelectedCard);
    const producerRole = metadata.producerRole;
    const globalUserFlag = MIGRATION_PRODUCER_ROLES.includes(producerRole);
    const isCustomer =
      userType?.toLowerCase() === "customer" ||
      producerRole?.toLowerCase() === "producer";

    const toTitleCase = (str) =>
      str.replace(
        /\w\S*/g,
        (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
      );

    const titleCaseVariant = toTitleCase(variant);
    const isProductCode2846 = String(productCode) === "2846";
    const medicareUinLookupKey = isProductCode2846
      ? toTitleCase(String(productName || ""))
      : titleCaseVariant;

    const renewalDetailsData = useSelector(getRenewalDetailsDataSelector);
    const isMedicareSelectPolicy = MEDICARE_SELECT_CODE.includes(String(renewalDetailsData?.subProductCode));
    const isMedicareSelectRevision = MEDICARE_SELECT_REVISION_CODE === String(renewalDetailsData?.subProductCode);
    const medicareSelectSubProductCode = String(renewalDetailsData?.subProductCode || "");
    // Medicare Select UIN depends on sub-product code, not the variant name.
    const medicareUinForVariant = isMedicareSelectPolicy
      ? MEDICARE_SELECT_SUBPRODUCT_UIN_MAP[medicareSelectSubProductCode]
      : PRODUCT_NAME_UIN_MAP_MEDICARE[medicareUinLookupKey];

    const [uinLabelMedicare, setUinLabelMedicare] = useState(
      t("r_quote_bottomline_health_3").replace(
        "{{UIN}}",
        medicareUinForVariant || ""
      )
    );
    const tataProductNameMedicare = t("r_quote_bottomline_health_2").replace(
      "{{prdNm}}",
      t(PRODUCT_NAME_LABEL_MAP_MEDICARE[medicareUinLookupKey])
    );
    const isNewUIN = Object.prototype.hasOwnProperty.call(
      PRICE_REVISION_VARIANT,
      titleCaseVariant?.replace(/\s/g, "").toLowerCase()
    );

    PremiumCompute.init(premiumSchemes, isCustomer);
    const migrationMapped = PremiumCompute.getMigrationMappedSchemes();
    const calculatedCustomPlan = useSelector(getCalculatedCustomPlan);
    // States
    const [selectedCard, setSelectedCard] = useState(null);

    const [tenureArr, setTenureArr] = useState([]);
    const [siArr, setSiArr] = useState([]);
    const isHealthSupercharge = String(productCode) === HEALTH_SUPERCHARGE_CODE;
    const [selectedTenure, setSelectedTenure] = useState("");
    const [selectedSumInsured, setSelectedSumInsured] = useState("");

    const [currentPlan, setCurrentPlan] = useState(null);
    const [recommendedPlan, setRecommendedPlan] = useState(null);
    const [customPlan, setCustomPlan] = useState(isHealthSupercharge ? (calculatedCustomPlan?.plan || null) : null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [packages, setPackages] = useState(null);
    const [viewCustomCard, setViewCustomCard] = useState(false);
    const [showAnimation, setShowAnimation] = useState(false);
    const lottieRef = useRef(null);
    const [lottieLoaded, setLottieLoaded] = useState(false);
    const [mandatoryPackageOptOut, setMandatoryPackageOptOut] = useState(false);
    const customPlanDirty = useSelector(getCustomPlanDirty);
    const migrationPlanDirty = useSelector(getMigrationPlanDirty);
    const selectedScheme = renewalDetailsData.premiumSchemes?.find((schema) => {
      return schema.schemeType.replace(/\s/g, "").toLowerCase() === "asis";
    });
    const utmMedium = useSelector(getIsApp);
    const productCodeFromStore = useSelector(getProductCode);
    const effectiveSelected = useMemo(() => {
      if (renewalDetailsData?.isGlobalSuperUser) {
        return selectedCard || propSelectedCard || "currentPlan";
      }
      return propSelectedCard || selectedCard;
    }, [propSelectedCard, selectedCard, renewalDetailsData?.isGlobalSuperUser]);
    const [showModal, setShowModal] = useState(false);
    const toggleAutoDebitModal = useCallback(() => {
      setShowModal((prev) => !prev);
    }, []);
    const [migDirty, setMigDirty] = useState(false);
    const migrationDetails = useSelector(getMigrationDetails);
    const [migProduct, setMigProduct] = useState(
      migrationDetails?.migrationProductCode || ""
    );
    const [migTenure, setMigTenure] = useState(
      migrationDetails?.migrationTenure || ""
    );
    const migSiOptions = migrationDetails?.migrationSIOptions || [];
    const migRoomOptions = migrationDetails?.migrationRoomOptions || [];
    const [migSI, setMigSI] = useState(migrationDetails?.migrationSI || "");

    const [migLoading, setMigLoading] = useState(false);
    const setCustomPlanState = useCallback(
      (plan = null, selectedTenure="", selectedSumInsured="", schemeType="") => {
        setCustomPlan(plan);
        dispatch(
          setCalculatedCustomPlan({
            isSelected: Boolean(plan),
            plan: plan,
            selectedTenure,
            selectedSumInsured,
            schemeType,
          })
        );
      },
      [dispatch]
    );

    useEffect(() => {
      if(isHealthSupercharge){
        setMigRoom(migrationDetails?.migrationRoomCategory || "");
      }
    }, [migrationDetails?.migrationRoomCategory]);

    useEffect(() => {
      if(isHealthSupercharge) {
        setCustomPlan(calculatedCustomPlan?.plan);
        setSelectedTenure(calculatedCustomPlan?.selectedTenure || "");
        setSelectedSumInsured(calculatedCustomPlan?.selectedSumInsured || "");
        if(effectiveSelected === "customisedPlan" && customPlan) {
          setAddOnPremium(calculatedCustomPlan?.plan?.totalPremium || customPlan?.totalPremium);
          setAddOnScheme(calculatedCustomPlan?.plan?.schemeTypeName || customPlan?.schemeTypeName);
        }
      }
    }, [calculatedCustomPlan?.plan]);

    
    const [migRoom, setMigRoom] = useState(
      (
        migrationDetails?.migrationRoomCategory ||
        renewalDetailsData?.roomCategory ||
        ""
      )
    );
    const [showRedirectModal, setShowRedirectModal] = useState(false);
    const initialSelectionSetRef = useRef(false);
    // Tracks whether the customer actually edited the custom plan (vs. the
    // auto-computed default), so the baseline resync effect below never masks
    // a real, intentional change.
    const userEditedCustomPlanRef = useRef(false);

    const handleRedirectModalOkay = () => {
      setShowRedirectModal(false);
        medicareBoltRedirection();
    };
    const buildPackagesCoversSnapshot = (packages = {}, covers = {}) => {
      const pkgTokens = Object.keys(packages)
        .filter((key) => packages[key]?.isOpted)
        .map((key) => `PKG:${key}`);

      const coverTokens = Object.entries(covers).flatMap(
        ([coverCode, cover]) => {
          if (!cover) return [];

          if (Array.isArray(cover.value)) {
            return cover.value
              .filter((opt) => opt?.opted)
              .map(
                (opt) => `COVER:${coverCode}:${String(opt?.name || "").trim()}`
              );
          }

          return cover?.opted ? [`COVER:${coverCode}`] : [];
        }
      );

      return [...pkgTokens, ...coverTokens].sort();
    };

    const { migrationPremium } = useSelector(getMigrationDetails);
    const migrateToOptions = useMemo(() => {
      const allowed =
        renewalDetailsData?.productMigrationData?.allowedMigrations || [];
      const bySchemeName = new Map(
        (migrationMapped || []).map((m) => [m.schemeTypeName, m])
      );

      const mapped = allowed
        .filter((row) => bySchemeName.has(row.schemeName))
        .map((row) => ({
          value: String(row.toProductCode),
          label: row.toProductName,
          schemeName: row.schemeName,
          plan: bySchemeName.get(row.schemeName),
        }));

      const CURRENT_SENTINEL = "__CURRENT_PRODUCT__";
      const currentOption = {
        value: CURRENT_SENTINEL,
        label: "Current",
        schemeName: "",
        plan: null,
      };

      return [currentOption, ...mapped];
    }, [
      renewalDetailsData?.productMigrationData?.allowedMigrations,
      migrationMapped,
    ]);

    const [customMigOpt, setCustomMigOpt] = useState(null);
    const canCalculateMigration =
      !!migProduct && !!migTenure && !!migSI && !migLoading && !!migRoom;
    const isAdjustToMedicareSelect = MEDICARE_SELECT_CODE.includes(String(migProduct));
    const handleMigrationChange = async (e) => {
      const newProductCode = e.target.value;
      const migrateSchemeObj = allowedMigrationsStable.find(
        (scheme) => String(scheme.toProductCode) === String(newProductCode)
      );
      const currentPlanSelected = migrateSchemeObj.schemeName === "Current";
      setMigProduct(newProductCode);
      setMigTenure("");
      setMigSI("");
      setMigRoom("");

      dispatch(
        setMigrationDetails({
          migrationProductCode: newProductCode,
          migrationTenure: "",
          migrationSI: "",
          migrationPremium: "",
          migrationSIOptions: [],
          migrationRoomOptions: [],
          migrationRoomCategory: "",
        })
      );

      if (!newProductCode) return;

      setMigLoading(true);
      try {
        const isMedicareSelectSmartPlan = renewalDetailsData?.variant.toLowerCase() === MEDICARE_SELECT_SMART_VARIANT_NAME.toLowerCase();
        const isMedicareSelectElitePlan = renewalDetailsData?.variant.toLowerCase() === MEDICARE_SELECT_ELITE_VARIANT_NAME.toLowerCase();

        const { data: body } = await axiosInstance.get(
          "/renewal/health/renewal-master",
          {
            params: {
              productCode: newProductCode,
              ...(isHealthSupercharge && {
                planName: renewalDetailsData?.variant,
              }),
              ...((isMedicareSelectSmartPlan || isMedicareSelectElitePlan) && {
                variant: renewalDetailsData?.variant,
              }),
            },
          }
        );

        const rows = Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body)
          ? body
          : [];

        const siData =
          rows.find(
            (x) => String(x?.TYPE).trim().toUpperCase() === "SUM_INSURED"
          )?.DATA ?? [];

        const opts = siData.map((v) => {
          const n = Number(String(v).replace(/,/g, ""));
          return { id: String(n), label: rupeesToLakhLabel(n) };
        });

        const roomData =
          rows.find(
            (x) => String(x?.TYPE).trim().toUpperCase() === "ROOM_CATEGORY"
          )?.DATA ?? [];
        const isGeoPlan = renewalDetailsData?.variant === GEO_PLAN;
        const isValuePlan = renewalDetailsData?.variant === VALUE_PLAN;
        const roomCategory =
          renewalDetailsData?.premiumSchemes
            ?.find((scheme) => scheme.schemeType === "As Is")
            ?.addonCovers?.C007?.value?.find((opt) => opt?.opted)?.name ||
          renewalDetailsData?.roomCategory ||
          "";
        const roomOpts = isValuePlan
          ? [
              {
                value: roomCategory || roomData?.[0] || "",
                label: roomCategory || roomData?.[0] || "",
              },
            ]
          : isGeoPlan
          ? (() => {
              // No room category -> default to Single Private
              if (!roomCategory) {
                return [
                  {
                    value: ROOM_CATEGORY_SINGLE_PRIVATE,
                    label: ROOM_CATEGORY_SINGLE_PRIVATE,
                  },
                ];
              }

              // Shared Accommodation -> allow switching to Single Private
              if (["shared accommodation", "shared accomodation"].includes(roomCategory?.toLowerCase())) {
                return roomData.map((name) => ({
                  value: String(name),
                  label: String(name),
                }));
              }

              // Single Private -> show only that value
              return [
                {
                  value: roomCategory,
                  label: roomCategory,
                },
              ];
            })()
          : roomData.map((name) => ({
              value: String(name),
              label: String(name),
            }));
        const preselectRoom =
          (roomCategory &&
            roomOpts.find((o) => o.value === roomCategory)
              ?.value) ||
          "";
        setMigRoom(preselectRoom);

        dispatch(
          setMigrationDetails({
            migrationSIOptions: opts,
            migrationRoomOptions: roomOpts,
            migrationRoomCategory: preselectRoom,
            isMigrationPolicy: !currentPlanSelected,
          })
        );
        setRemarkMigrationPolicy(!currentPlanSelected);
      } catch (err) {
        console.log(err);
      } finally {
        setMigLoading(false);
      }
    };

    const migrationTenureDisabled = !migProduct;
    const migrationSiDisabled =
      !migProduct || migLoading || migSiOptions.length === 0;

    const handleMigrationCalculate = async () => {
      if (!migProduct || !migTenure || !migSI || migLoading) return;

      setMigLoading(true);
      try {
        const sel = allowedMigrationsStable.find(
          (m) => String(m.toProductCode) === String(migProduct)
        );
        const migrationPackageList = Object.values(
          renewalDetailsData?.premiumSchemes?.find(
            (schemes) => schemes.schemeType === sel?.schemeName
          )?.packages || {}
        )
          .filter((packageData) => packageData.isOpted === true)
          .map((packageData) => packageData.name);
        const payload = {
          policyNo: renewalDetailsData?.policyNo ?? "",
          tenure: Number(migTenure),
          sumInsured: Number(migSI),
          fromProductCode: String(
            sel?.fromProductCode ?? renewalDetailsData?.productCode ?? ""
          ),
          toProductCode: String(migProduct),
          migrationSchemeName:
            sel?.schemeName !== "" ? sel?.schemeName : "Current",
          ...(migRoom ? { roomCategory: migRoom } : {}),
          packageList: sel?.fromProductCode !== "" ? migrationPackageList || [] : currentPlan?.packageList || [],
        };

        const hasAccidentalCover = renewalDetailsData.accidentalDeathRider;

        const { data } = await axiosInstance.post(
          "/renewal/health/calculatePremium",
          payload
        );

        const responseData = data?.data ?? data;

        const mapped = PremiumCompute.mapCalculatedPremium(
          responseData?.premiumDtls,
          {
            schemeTypeName: responseData?.newSchemeName,
            tenure: payload.tenure,
            sumInsured: payload.sumInsured,
            isMedicareSelectPolicy,
          }
        );

        dispatch(
          savePreComputedPackagesCovers({
            cardType: "migrationPlan",
            packages: mapped?.packages || {},
            covers: mapped?.addonCovers || {},
          })
        );

        let isC005AddedInTotalPremium = false;
        let addedC005Value = 0;
        let addedC005GstVal = 0;

        if (
          !hasAccidentalCover &&
          // mapped?.ignoredAddonCovers?.C005?.opted &&
          mapped.packageList?.includes("Accident Suraksha") &&
          mapped.schemeTypeNameTn !== "exceptionas-is"
        ) {
          addedC005Value = Number(mapped?.ignoredAddonCovers?.C005?.value) || 0;
          addedC005GstVal =
            Number(mapped?.ignoredAddonCovers?.C005?.gstVal) || 0;

          mapped.totalPremiumInDecimal =
            Number(mapped.totalPremiumInDecimal || 0) +
            addedC005Value +
            addedC005GstVal;

          mapped.totalPremium = Math.round(mapped.totalPremiumInDecimal);

          isC005AddedInTotalPremium = true;
        }

        mapped.isC005AddedInTotalPremium = isC005AddedInTotalPremium;
        mapped.addedC005Value = addedC005Value;
        mapped.addedC005GstVal = addedC005GstVal;

        dispatch(
          setMigrationDetails({
            migrationProductCode: payload.toProductCode,
            migrationTenure: String(payload.tenure),
            migrationSI: String(payload.sumInsured),
            migrationPremium: mapped,
            migrationSchemeName: responseData?.newSchemeName,
            migrationFromProductCode: sel?.fromProductCode,
            migrationToProductCode: sel?.toProductCode,
            isMigrationPolicySelected: true,
          })
        );

        setSelectedCard("migrationPlan");
        dispatch(setPropSelectedCard("migrationPlan"));
        setSelectedPlan(mapped);
        setAddOnPremium(mapped.totalPremium);
        setMigDirty(false);
      } catch (err) {
        console.error("calculatePremium failed", err);
      } finally {
        setMigLoading(false);
      }
    };

    const allowedMigrationsStable = useMemo(() => {
      const fromApi =
        renewalDetailsData?.productMigrationData?.allowedMigrations;

      const list = Array.isArray(fromApi) ? [...fromApi] : [];

      const extra = {
        fromProductCode: "",
        toProductCode: renewalDetailsData.subProductCode || renewalDetailsData.productCode,
        toProductName: "Current",
        schemeName: "Current",
        migrationSchemeName: "Current",
      };

      return [...list, extra];
    }, [renewalDetailsData?.productMigrationData?.allowedMigrations]);

    useEffect(() => {
      if (!lottieLoaded) {
        const script = document.createElement("script");
        script.src =
          "https://unpkg.com/@dotlottie/player-component@latest/dist/dotlottie-player.mjs";
        script.type = "module";
        script.onload = () => {
          setLottieLoaded(true);
        };
        document.head.appendChild(script);
      }
    }, [lottieLoaded]);

    useEffect(() => {
      if (lottieLoaded && lottieRef.current && showAnimation) {
        lottieRef.current.innerHTML = `
        <dotlottie-player
          id="lottiePlayer"
          src="https://lottie.host/bf87b206-1711-438a-a40c-c0afe9def8d2/1OMzEJrie8.json"
          background="transparent"
          speed="1"
          style="width: 200px; height: 200px;"
          autoplay>
        </dotlottie-player>
      `;

        const lottiePlayer = document.getElementById("lottiePlayer");
        if (lottiePlayer) {
          lottiePlayer.addEventListener("complete", handleLottieComplete);
        }

        return () => {
          if (lottiePlayer) {
            lottiePlayer.removeEventListener("complete", handleLottieComplete);
          }
        };
      }
    }, [lottieLoaded, showAnimation]);
    const invokeMoengageAnalyticsFunctionUpsell = () => {
      invokeMoengageAnalytics(
        EVENTS.EVENT_RENEWAL_QUOTE_PAGE_UPSELL_PLAN_SELECTION,
        {
          "Policy No": renewalDetailsData?.policyNo || "",
          "Product Name": renewalDetailsData?.productName || "",
          "Product Code":
            renewalDetailsData?.productCode?.toString() ||
            productCodeFromStore?.toString() ||
            "",
          "Mobile Number":
            renewalDetailsData?.proposerDetails?.mobileNumber || "",
          DOB: renewalDetailsData?.proposerDetails?.dob || "",
          Variant: renewalDetailsData?.variant || "",
          // "Scheme Type": selectedScheme?.schemeType || "",
          "Total Premium": customPlan?.totalPremium || "",
          Package: Object.values(customPlan?.packages || {})
            .filter((pkg) => pkg.isOpted && pkg.isMandatory)
            .map((pkg) => pkg?.name),
          Tenure: customPlan?.tenureSI?.tenure || "",
          "Sum Insured": customPlan?.tenureSI?.sumInsured || "",
          "Producer Code": renewalDetailsData?.producerCode || "",
          is_app: utmMedium?.toLowerCase()?.includes("mobile_app")
            ? "Yes"
            : "No",
        }
      );
    };

    const handleClick = () => {
      if (isCustomer) {
        invokeMoengageAnalyticsFunctionUpsell();
      }
      setShowAnimation(true);
    };

    const updateCustomPlan = (tenureId, siId, tenureSiSchemeMap) => {
      const planKey = `${tenureId}${siId}`;
      const plan = tenureSiSchemeMap[planKey];
      if (!plan) {
        setCustomPlan(null);
        return;
      }

      const selectedPackages = Object.entries(plan.packages || {})
        .filter(([_, pkg]) => (pkg?.isMandatory || pkg?.isOpted) && pkg?.value !== 0)
        .map(([key]) => key);

      const rawCustomPlan = PremiumCompute.compute(
        plan.schemeTypeName,
        selectedPackages,
        plan.addonCovers,
        isMedicareSelectPolicy
      );

      const customPlan = {
        ...JSON.parse(JSON.stringify(rawCustomPlan)),
        packages: Object.fromEntries(
          Object.entries(plan.packages || {}).map(([k, v]) => [k, { ...v }])
        ),
      };
      
      const hasAccidentalCover = renewalDetailsData.accidentalDeathRider;

      const matchingScheme = premiumSchemes.find(
        (ps) => ps.schemeType === customPlan.schemeTypeName
      );

      if (
        !hasAccidentalCover &&
        // matchingScheme?.addonCovers?.C005?.opted &&
        customPlan.packageList?.includes("Accident Suraksha") &&
        customPlan.schemeTypeNameTn !== "exceptionas-is"
      ) {
        const c005Value = Number(matchingScheme?.addonCovers?.C005?.value) || 0;
        const c005GstVal = Number(matchingScheme?.addonCovers?.C005?.gstVal) || 0;

        customPlan.totalPremium = Math.round(
          (customPlan.totalPremiumInDecimal || 0) + c005Value + c005GstVal
        );
      }
      setCustomPlan(customPlan);
      if (customPlan) {
        dispatch(
          savePreComputedPackagesCovers({
            cardType: "customPlan",
            packages: customPlan?.packages || {},
            covers: customPlan?.addonCovers || {},
          })
        );
      }

      if (selectedCard === "customisedPlan") {
        if (isCustomer) {
          invokeMoengageAnalytics(
            EVENTS.EVENT_RENEWAL_QUOTE_PAGE_UPSELL_PLAN_SELECTION,
            {
              "Policy No": renewalDetailsData?.policyNo || "",
              "Product Name": renewalDetailsData?.productName || "",
              "Product Code":
                renewalDetailsData?.productCode?.toString() ||
                productCodeFromStore?.toString() ||
                "",
              "Mobile Number":
                renewalDetailsData?.proposerDetails?.mobileNumber || "",
              DOB: renewalDetailsData?.proposerDetails.dob,
              Variant: renewalDetailsData?.variant || "",
              // "Scheme Type": selectedScheme?.schemeType || "",
              "Total Premium": customPlan.totalPremium || "",
              Package: Object.values(selectedScheme?.packages || {})
                .filter((pkg) => pkg.isOpted && pkg.isMandatory)
                .map((pkg) => pkg?.name),
              Tenure: customPlan.tenureSI?.tenure || "",
              "Sum Insured": customPlan.tenureSI?.sumInsured || "",
              is_app: utmMedium?.toLowerCase()?.includes("mobile_app")
                ? "Yes"
                : "No",
            }
          );
        }

        setAddOnPremium(customPlan.totalPremium);
        setAddOnScheme(customPlan.schemeTypeName);
        setSelectedPackagesArr(customPlan.packageList || []);
      }
    };

    useEffect(() => {
      const findCurrentPlan = () => {
        const plan = premiumSchemes.find(
          (scheme) => scheme.schemeType === "As Is"
        );
        setPackages(plan?.packages || []);

        const expiringDateStr = PRICE_REVISION_CUTOFF_DATE.JULY_POST;
        const isGlobalUser = globalUserFlag;

        let rawAsIsPlan;
        if (isGlobalUser && expiringDateStr) {
          const date = new Date(expiringDateStr);
          const month = date.getMonth();
          const day = date.getDate();

          const isInJuneOrJulyRange =
            (month === 5 && day >= 1) || (month === 6 && day <= 27);

          rawAsIsPlan = isInJuneOrJulyRange
            ? PremiumCompute.getExceptionAsIs()
            : PremiumCompute.getAsIs();
        } else {
          rawAsIsPlan = PremiumCompute.getAsIs();
        }

        const asIsPlan = JSON.parse(JSON.stringify(rawAsIsPlan));

        const hasAccidentalCover = renewalDetailsData.accidentalDeathRider;
        const matchingScheme = premiumSchemes.find(
          (scheme) => scheme.schemeType === asIsPlan.schemeTypeName
        );

        if (
          !hasAccidentalCover &&
          // matchingScheme?.addonCovers?.C005?.opted &&
          asIsPlan.packageList?.includes("Accident Suraksha") &&
          asIsPlan.schemeTypeNameTn !== "exceptionas-is"
        ) {
          const c005Value = Number(matchingScheme?.addonCovers?.C005?.value) || 0;
          const c005GstVal =
            Number(matchingScheme?.addonCovers?.C005?.gstVal) || 0;
          asIsPlan.totalPremium = Math.round(
            (asIsPlan.totalPremiumInDecimal || 0) + c005Value + c005GstVal
          );
        }

        setCurrentPlan(asIsPlan);
        return asIsPlan;
      };

      const initializeTenureAndSiLists = () => {
        const customScheme = PremiumCompute.getInitMapping();
        const sortedTenureList = [...customScheme.tenureList].sort(
          (a, b) => a.value - b.value
        );
        const sortedSiList = [...customScheme.siList].sort(
          (a, b) => a.value - b.value
        );
        setTenureArr(sortedTenureList);
        setSiArr(sortedSiList);
        setSelectedTenure(sortedTenureList[0]?.id);
        setSelectedSumInsured(sortedSiList[0]?.id);
        updateCustomPlan(
          sortedTenureList[0]?.id,
          sortedSiList[0]?.id,
          customScheme.tenureSiSchemeMap
        );
      };

      const setupRecommendedScheme = (curPlan) => {
        const getRecommended = () => {
          const expiryDate = new Date(PRICE_REVISION_CUTOFF_DATE.JULY_POST);
          const day = expiryDate.getDate();
          const month = expiryDate.getMonth();
          const year = expiryDate.getFullYear();

          const isMay2025 = year === 2025 && month === 4;
          const isJuneOrJulyRange2025 =
            year === 2025 &&
            ((month === 5 && day >= 1) || (month === 6 && day <= 27));
          const cutoffDate = new Date(PRICE_REVISION_CUTOFF_DATE.JULY);
          const isPostJuly27 = expiryDate > cutoffDate;

          const variantWithoutCase = titleCaseVariant
            ?.replace(/\s/g, "")
            .toLowerCase();
          const isNewUIN = Object.prototype.hasOwnProperty.call(
            PRICE_REVISION_VARIANT,
            variantWithoutCase
          );
          const isPremier = titleCaseVariant?.includes("Premier");

          const setUINLabel = (labelPrefix) => {
            setUinLabelMedicare(
              t("r_quote_bottomline_health_3").replace(
                "{{UIN}}",
                NEW_PRODUCT_NAME_UIN_MAP_MEDICARE[labelPrefix] || ""
              )
            );
          };

          if (globalUserFlag) {
            if (isMay2025) {
              const labelPrefix = isPremier ? "Premier - Medicare" : "Medicare";
              setUINLabel(labelPrefix);
              return PremiumCompute.getExceptionAsIs();
            }
            if (isJuneOrJulyRange2025 && isNewUIN) {
              const labelPrefix = PRICE_REVISION_VARIANT[variantWithoutCase];
              setUINLabel(labelPrefix);
              return PremiumCompute.getAsIs();
            }
            if (isPostJuly27 && isNewUIN) {
              const labelPrefix = PRICE_REVISION_VARIANT[variantWithoutCase];
              setUINLabel(labelPrefix);
              return PremiumCompute.getRecommended();
            }
          }

          if (isCustomer) {
            const mayCutoffDate = new Date(PRICE_REVISION_CUTOFF_DATE.MAY);
            const isPostMay2025 = expiryDate > mayCutoffDate;
            if (isPostMay2025 && isNewUIN) {
              const labelPrefix = PRICE_REVISION_VARIANT[variantWithoutCase];
              setUINLabel(labelPrefix);
            }
          }

          return PremiumCompute.getRecommended();
        };

        const valid = [
          "currentPlan",
          "recommendedPlan",
          "customisedPlan",
          "migrationPlan",
        ];
        if (valid.includes(propSelectedCard)) {
          const recOnce = getRecommended();

          let adjustedRecommended = null;

          if (recOnce) {
            const matchOnce = premiumSchemes.find(
              (scheme) => scheme.schemeType === recOnce?.schemeTypeName
            );

            adjustedRecommended = JSON.parse(JSON.stringify(recOnce));

            if (matchOnce) adjustedRecommended.packages = matchOnce.packages;

            const hasAccidental = renewalDetailsData.accidentalDeathRider;

            if (
              !hasAccidental &&
              // matchOnce?.addonCovers?.C005?.opted &&
              adjustedRecommended.packageList?.includes("Accident Suraksha") &&
              adjustedRecommended.schemeTypeNameTn !== "exceptionas-is"
            ) {
              const c005Value = Number(matchOnce?.addonCovers?.C005?.value) || 0;
              const c005GstVal = Number(matchOnce?.addonCovers?.C005?.gstVal) || 0;

              adjustedRecommended.totalPremium = Math.round(
                (Number(adjustedRecommended.totalPremiumInDecimal) || 0) +
                  c005Value +
                  c005GstVal
              );
            }

            setRecommendedPlan(adjustedRecommended);
          } else {
            setRecommendedPlan(null);
          }

          const chosen = curPlan;
          const chosenCard = "currentPlan";
          const recommendedSI = Number(
            adjustedRecommended?.tenureSI?.shortSumInsured
          );
          const currentSI = Number(curPlan?.tenureSI?.shortSumInsured);

          if (
            Number.isFinite(recommendedSI) &&
            Number.isFinite(currentSI) &&
            recommendedSI > currentSI
          ) {
            setCustomerUpsellFlag(true);
            setCustomerRecommendedPlan(adjustedRecommended);
          }

          setSelectedCard(chosenCard);
          dispatch(setPropSelectedCard(chosenCard));
          setSelectedPlan(chosen);
          setSelectedPackagesArr(chosen?.packageList || []);
          setAddOnScheme(chosen?.schemeTypeName);
          setAddOnPremium(chosen?.totalPremium ?? "");
          return;
        }
        const rec = getRecommended();
        if (rec) {
          const match = premiumSchemes.find(
            (scheme) => scheme.schemeType === rec?.schemeTypeName
          );
          const schemeClone = JSON.parse(JSON.stringify(rec));
          if (match) schemeClone.packages = match.packages;

          const hasAccidental = renewalDetailsData.accidentalDeathRider;
          if (
            !hasAccidental &&
            // match?.addonCovers?.C005?.opted &&
            schemeClone.packageList?.includes("Accident Suraksha") &&
            schemeClone.schemeTypeNameTn !== "exceptionas-is"
          ) {
            const c005Value = Number(match?.addonCovers?.C005?.value) || 0;
            const c005GstVal = Number(match?.addonCovers?.C005?.gstVal) || 0;
            schemeClone.totalPremium = Math.round(
              (schemeClone.totalPremiumInDecimal || 0) + c005Value + c005GstVal
            );
          }

          setRecommendedPlan(schemeClone);

          const migrationEligibleLocal =
            !!renewalDetailsData?.isGlobalSuperUser;

          if (migrationEligibleLocal && propSelectedCard !== "migrationPlan") {
            setSelectedPlan(curPlan);
            setSelectedPackagesArr(curPlan?.packageList || []);
            setAddOnScheme(curPlan?.schemeTypeName);
            setAddOnPremium(curPlan?.totalPremium);
            if (!propSelectedCard) setSelectedCard("currentPlan");
            return;
          }
          if (!migrationEligibleLocal) {
            setSelectedPlan(curPlan);
            setSelectedPackagesArr(curPlan?.packageList || []);
            setAddOnScheme(curPlan?.schemeTypeName);
            setAddOnPremium(curPlan?.totalPremium);

            if (!propSelectedCard) setSelectedCard("currentPlan");
          }
          setAddOnScheme(curPlan?.schemeTypeName);
          setAddOnPremium(curPlan?.totalPremium);

          // const recommendedSI = schemeClone?.tenureSI?.shortSumInsured;
          // const currentSI = curPlan?.tenureSI?.shortSumInsured;
          // if (recommendedSI > currentSI) {
          //   setCustomerRecommendedPlan(schemeClone);
          //   setCustomerUpsellFlag(false);
          //   setSelectedPlan(schemeClone);
          // }
          return;
        }          

        // No recommended available -> default to CURRENT
        setRecommendedPlan(null);
        setSelectedPlan(curPlan);
        setSelectedCard("currentPlan");
        setSelectedPackagesArr(curPlan?.packageList || []);
        setAddOnScheme(curPlan?.schemeTypeName);
        setAddOnPremium(curPlan?.totalPremium);
        if (!propSelectedCard) {
          setSelectedCard("currentPlan");
          dispatch(setPropSelectedCard("currentPlan"));
        }

        if (!globalUserFlag) {
          const customScheme = PremiumCompute.getInitMapping();
          const sortedSiList = [...customScheme.siList].sort(
            (a, b) => a.value - b.value
          );
          if (curPlan && sortedSiList.length > 1) {
            const planKey = `${curPlan.tenureSI.tenureId}${sortedSiList[1]?.id}`;
            const upsellPlan = customScheme.tenureSiSchemeMap[planKey];
            if (upsellPlan) {
              const customerUpsellPlan = PremiumCompute.compute(
                upsellPlan.schemeTypeName,
                upsellPlan.packagesSelected,
                upsellPlan.addonCovers,
                isMedicareSelectPolicy
              );
              setCustomerRecommendedPlan(customerUpsellPlan);
              setCustomerUpsellFlag(true);
            }
          }
          setUinLabelMedicare(
            t("r_quote_bottomline_health_3").replace(
              "{{UIN}}",
              medicareUinForVariant || ""
            )
          );
        }
      };

      const cp = findCurrentPlan();
      initializeTenureAndSiLists();
      if (renewalDetailsData?.isGlobalSuperUser) {
        setSelectedPlan(cp);
        setSelectedPackagesArr(cp?.packageList || []);
        setAddOnScheme(cp?.schemeTypeName);
        setAddOnPremium(cp?.totalPremium);
      } else {
        setupRecommendedScheme(cp);
      }
      if (
        renewalDetailsData?.isGlobalSuperUser &&
        propSelectedCard === "migrationPlan"
      ) {
        setAddOnPremium("");
      }
    }, []); // runs once on mount

    const handleCardSelection = useCallback(
      (value, plan, renewalDetailsData) => {
        const isCurrent = value === "currentPlan";
        const isCustom = value === "customisedPlan";
        const isRecommended = value === "recommendedPlan";
        const isMigration = value === "migrationPlan";
        if (isMigration) {
          const sel = allowedMigrationsStable.find(
            (m) => String(m.toProductCode) === String(migProduct)
          );
          const isCurrentMigration =
            sel?.schemeName === "Current" ||
            String(migProduct) === String(renewalDetailsData?.subProductCode);

          const flag =
            migrationPremium?.isMigrationPolicy != null
              ? Boolean(migrationPremium.isMigrationPolicy)
              : !isCurrentMigration;

          dispatch(
            setMigrationDetails({
              isMigrationPolicy: flag,
              isMigrationPolicySelected: true,
            })
          );
          setRemarkMigrationPolicy(flag);
        } else {
          dispatch(
            setMigrationDetails({
              isMigrationPolicy: false,
              isMigrationPolicySelected: false,
            })
          );
          setRemarkMigrationPolicy(false);
        }

        setAddOnScheme(plan?.schemeTypeName);
        setSelectedPackagesArr(plan?.packageList || []);
        setAddOnPremium(plan?.totalPremium);
        setSelectedCard(value);
        dispatch(setPropSelectedCard(value));
        setSelectedPlan(plan);

        const expiringDateStr = PRICE_REVISION_CUTOFF_DATE.JULY_POST;
        let applyGlobalUin = false;
        const variantWithoutCase = titleCaseVariant
          ?.replace(/\s/g, "")
          .toLowerCase();

        const isNewUIN = Object.prototype.hasOwnProperty.call(
          PRICE_REVISION_VARIANT,
          variantWithoutCase
        );

        if (expiringDateStr) {
          const expiryDate = new Date(expiringDateStr);
          const day = expiryDate.getDate();
          const month = expiryDate.getMonth();
          const year = expiryDate.getFullYear();

          const isMay2025 = year === 2025 && month === 4;
          const isJuneOrJulyRange2025 =
            year === 2025 &&
            ((month === 5 && day >= 1) || (month === 6 && day <= 27));

          const cutoffDate = new Date(PRICE_REVISION_CUTOFF_DATE.JULY);
          const isPostJuly27 = expiryDate > cutoffDate;

          if (isMay2025 && globalUserFlag && isRecommended && isNewUIN) {
            applyGlobalUin = true;
          } else if (
            isJuneOrJulyRange2025 &&
            globalUserFlag &&
            (isCustom || isRecommended) &&
            isNewUIN
          ) {
            applyGlobalUin = true;
          } else if (isPostJuly27 && isNewUIN) {
            applyGlobalUin = true;
          }
        }

        const labelPrefix = PRICE_REVISION_VARIANT[variantWithoutCase];
        const uinMap = applyGlobalUin
          ? NEW_PRODUCT_NAME_UIN_MAP_MEDICARE[labelPrefix]
          : medicareUinForVariant;

        setUinLabelMedicare(
          t("r_quote_bottomline_health_3").replace("{{UIN}}", uinMap || "")
        );

        if (isCurrent && isCustomer) {
          invokeMoengageAnalytics(
            EVENTS.EVENT_RENEWAL_QUOTE_PAGE_DEFAULT_PLAN_SELECTION,
            {
              "Policy No": renewalDetailsData?.policyNo || "",
              "Product Name": renewalDetailsData?.productName || "",
              "Product Code":
                renewalDetailsData?.productCode?.toString() ||
                productCodeFromStore?.toString() ||
                "",
              "Mobile Number":
                renewalDetailsData?.proposerDetails?.mobileNumber || "",
              DOB: renewalDetailsData?.proposerDetails?.dob || "",
              Variant: renewalDetailsData?.variant || "",
              "Scheme Type": selectedScheme?.schemeType || "",
              "Total Premium": selectedScheme?.premium,
              Package: Object.values(selectedScheme?.packages || {})
                .filter((pkg) => pkg.isOpted && pkg.isMandatory)
                .map((pkg) => pkg?.name),
              Tenure: selectedScheme?.tenure || "",
              "Sum Insured":
                renewalDetailsData?.memberDetails?.[0]?.applicableSI || "",
              "Producer Code": renewalDetailsData?.producerCode || "",
              is_app: utmMedium?.toLowerCase()?.includes("mobile_app")
                ? "Yes"
                : "No",
            }
          );
        }

        if (isRecommended && isCustomer) {
          invokeMoengageAnalytics(
            EVENTS.EVENT_RENEWAL_QUOTE_PAGE_RECOMMENDED_PLAN_SELECTION,
            {
              "Policy No": renewalDetailsData?.policyNo || "",
              "Product Name": renewalDetailsData?.productName || "",
              "Product Code":
                renewalDetailsData?.productCode?.toString() ||
                productCodeFromStore?.toString() ||
                "",
              "Mobile Number":
                renewalDetailsData?.proposerDetails?.mobileNumber || "",
              DOB: renewalDetailsData?.proposerDetails?.dob || "",
              Variant: renewalDetailsData?.variant || "",
              "Scheme Type": recommendedPlan?.schemeTypeName || "",
              "Total Premium": recommendedPlan?.totalPremium || "",
              Package: Object.values(recommendedPlan?.packages || {})
                .filter((pkg) => pkg.isOpted && pkg.isMandatory)
                .map((pkg) => pkg?.name),
              Tenure: recommendedPlan?.tenureSI?.tenure || "",
              "Sum Insured": recommendedPlan?.tenureSI?.sumInsured || "",
              "Producer Code": renewalDetailsData?.producerCode || "",
              is_app: utmMedium?.toLowerCase()?.includes("mobile_app")
                ? "Yes"
                : "No",
            }
          );
        }

        if (isCustom && isCustomer) {
          invokeMoengageAnalyticsFunctionUpsell();
        }

        if (isMigration && migrationPremium) {
          setAddOnScheme(migrationPremium.schemeTypeName);
          setSelectedPackagesArr(migrationPremium.packageList || []);
          setAddOnPremium(migrationPremium.totalPremium);
          setSelectedPlan(migrationPremium);
        }

        const recommendedSI = recommendedPlan?.tenureSI?.shortSumInsured;
        const currentSI = currentPlan?.tenureSI?.shortSumInsured;

        if (isCurrent && recommendedSI > currentSI) {
          setCustomerUpsellFlag(true);
          setCustomerRecommendedPlan(recommendedPlan);
        } else if (
          isCustom &&
          selectedSumInsured === siArr?.[0]?.id &&
          siArr?.length > 1
        ) {
          const customScheme = PremiumCompute.getInitMapping();
          const planKey = `${plan.tenureSI.tenureId}${siArr[1].id}`;
          const upsellPlan = customScheme.tenureSiSchemeMap?.[planKey];

          if (upsellPlan) {
            const selectedPackages = Object.keys(
              upsellPlan.packages || {}
            ).filter(
              (pkgKey) =>
                upsellPlan.packages[pkgKey]?.isOpted &&
                upsellPlan.packages[pkgKey]?.isMandatory
            );
            const customerUpsellPlan = PremiumCompute.compute(
              upsellPlan.schemeTypeName,
              selectedPackages,
              upsellPlan.addonCovers,
              isMedicareSelectPolicy
            );
            const hasAccidentalRider = renewalDetailsData?.accidentalDeathRider;
            const hasAccidentSuraksha =
              selectedPackages.includes("ACCIDENT_SURAKSHA") ||
              selectedPackages.includes("Accident Suraksha");
            const matchingScheme = premiumSchemes.find(
              (ps) => ps.schemeType === customerUpsellPlan.schemeTypeName
            );
            if (
              !hasAccidentalRider &&
              hasAccidentSuraksha
            ) {
              const c005Val = +matchingScheme?.addonCovers?.C005?.value || 0;
              const c005Gst = +matchingScheme?.addonCovers?.C005?.gstVal || 0;

              customerUpsellPlan.totalPremium = Math.round(
                (customerUpsellPlan.totalPremiumInDecimal || 0) +
                  c005Val +
                  c005Gst
              );
            }
            setCustomerRecommendedPlan(customerUpsellPlan);
            setCustomerUpsellFlag(true);
          }
        } else if (isRecommended && recommendedSI > currentSI) {
          setCustomerUpsellFlag(false);
        }
      },
      [
        setAddOnScheme,
        setSelectedPackagesArr,
        setAddOnPremium,
        recommendedPlan,
        currentPlan?.tenureSI?.shortSumInsured,
        selectedSumInsured,
        siArr,
        setCustomerUpsellFlag,
        setCustomerRecommendedPlan,
        selectedScheme,
        renewalDetailsData,
        titleCaseVariant,
      ]
    );

    const openSelectedSchemeModal = (selectedPlan) => {
      const premiumBreakup = {
        basePremium: selectedPlan.basePremium,
        totalDiscountAmount: selectedPlan.totalDiscount,
        netPremium: selectedPlan.premiumWithoutGST,
        gstValue: selectedPlan.premiumGSTVal,
        policyTotalPremium: selectedPlan.premium,
        packagePremium: selectedPlan.packagePremiumWithGST,
        totalPremium: selectedPlan.totalPremium,
        expiringPolicyToDate: renewalDetailsData?.expiringPolicyToDate,
        sgstVal: 0,
        cgstVal: 0,
        igstVal: 0,
      };
      setPremiumDataToShow(premiumBreakup);
      setShowPremiumBreakupModal(true);
    };

    const displayPremiumAmount = (premium, tenure) => (
      <div className={classNames("flexCenter", rupeeFontStyle)}>
        <aside>
          ₹ {toAmount(premium)} &nbsp;
          {tenure > 1 && (
            <span>
              &nbsp;
              <Image
                src={`${IMAGE_BASE_PATH}/images/Separator-in-card.svg`}
                alt="X"
                width={10}
                height={28}
              />{" "}
              <span className="upsell-prem upsellPerPos">
                ₹{toAmount(Math.round(premium / tenure))} /{" "}
                <span className="upsell-value">Year</span>
              </span>
            </span>
          )}
        </aside>
      </div>
    );

    const handleTenureChange = (tenureId) => {
      setSelectedTenure(tenureId);
      updateCustomPlan(
        tenureId,
        selectedSumInsured,
        PremiumCompute.getInitMapping().tenureSiSchemeMap
      );
      if (
        selectedCard === "customisedPlan" &&
        siArr.length > 1 &&
        selectedSumInsured === siArr[0].id
      ) {
        const customScheme = PremiumCompute.getInitMapping();
        const basePlanKey = `${tenureId}${selectedSumInsured}`;
        const basePlanObj = customScheme.tenureSiSchemeMap[basePlanKey];

        if (basePlanObj) {
          const basePlan = PremiumCompute.compute(
            basePlanObj.schemeTypeName,
            basePlanObj.packagesSelected,
            basePlanObj.addonCovers,
            isMedicareSelectPolicy
          );
          const nextSumInsuredPlanKey = `${basePlan.tenureSI.tenureId}${siArr[1].id}`;
          const nextSumInsuredPlan =
            customScheme.tenureSiSchemeMap[nextSumInsuredPlanKey];

          if (nextSumInsuredPlan) {
            const upsellPlan = PremiumCompute.compute(
              nextSumInsuredPlan.schemeTypeName,
              nextSumInsuredPlan.packagesSelected,
              nextSumInsuredPlan.addonCovers,
              isMedicareSelectPolicy
            );
            setCustomerRecommendedPlan(upsellPlan);
            setCustomerUpsellFlag(true);
          }
        }
      }
    };

    const handleSumInsuredChange = (siId) => {
      if (siArr[0].id !== siId) {
        setCustomerUpsellFlag(false);
      } else {
        setCustomerUpsellFlag(true);
      }
      setSelectedSumInsured(siId);
      updateCustomPlan(
        selectedTenure,
        siId,
        PremiumCompute.getInitMapping().tenureSiSchemeMap
      );
    };

    const getSafePackageList = (plan) =>
      Array.isArray(plan?.packageList) ? plan.packageList : [];

    const isCoverVisible = (cover) => {
      if (!Array.isArray(cover?.value)) {
        return cover?.opted === true && cover?.value !== 0;
      }
      return cover.value.some(
        (item) => item?.opted === true && item?.value !== 0
      );
    };

    const preComputedPackagesCovers = useSelector(getPreComputedPackagesCovers);

    // Something outside this component (e.g. a duplicate quote refetch) can reset
    // the redux customPlan baseline back to []. If the customer never actually
    // edited the custom plan, re-establish the baseline instead of letting it look
    // "dirty" and hide the premium.
    useEffect(() => {
      if (!isMedicareSelectPolicy || !customPlan || userEditedCustomPlanRef.current) {
        return;
      }

      const currentSnap = buildPackagesCoversSnapshot(
        customPlan?.packages,
        customPlan?.addonCovers
      );
      const baselineSnap = preComputedPackagesCovers?.customPlan || [];

      if (JSON.stringify(baselineSnap) !== JSON.stringify(currentSnap)) {
        dispatch(
          savePreComputedPackagesCovers({
            cardType: "customPlan",
            packages: customPlan?.packages || {},
            covers: customPlan?.addonCovers || {},
          })
        );
      }
    }, [isMedicareSelectPolicy, customPlan, preComputedPackagesCovers?.customPlan]);

    const mergeAddonCoverSelectionState = (
      calculatedCovers = {},
      selectedCovers = {}
    ) => {
      const coverKeys = new Set([
        ...Object.keys(calculatedCovers || {}),
        ...Object.keys(selectedCovers || {}),
      ]);

      return Array.from(coverKeys).reduce((acc, key) => {
        const calculatedCover = calculatedCovers?.[key] || {};
        const selectedCover = selectedCovers?.[key] || {};
        const nextCover = {
          ...calculatedCover,
          opted:
            typeof selectedCover.opted === "boolean"
              ? selectedCover.opted
              : calculatedCover.opted,
          isIncludedCover:
            typeof selectedCover.isIncludedCover === "boolean"
              ? selectedCover.isIncludedCover
              : calculatedCover.isIncludedCover,
        };

        if (
          Array.isArray(calculatedCover.value) ||
          Array.isArray(selectedCover.value)
        ) {
          const calculatedOptions = Array.isArray(calculatedCover.value)
            ? calculatedCover.value
            : selectedCover.value || [];
          const selectedOptions = Array.isArray(selectedCover.value)
            ? selectedCover.value
            : [];

          nextCover.value = calculatedOptions.map((option, index) => {
            const selectedOption =
              selectedOptions.find((item) => item?.name === option?.name) ||
              selectedOptions[index];

            return {
              ...option,
              opted:
                typeof selectedOption?.opted === "boolean"
                  ? selectedOption.opted
                  : option?.opted,
            };
          });
        }

        acc[key] = nextCover;
        return acc;
      }, {});
    };

    const handlePackageUpdate = async (updatedPackages, updatedCovers = {}) => {
      userEditedCustomPlanRef.current = true;
      const optedPackages = Object.keys(updatedPackages).filter(
        (key) => updatedPackages[key].isOpted && updatedPackages[key].value !== 0,
      );

      const optedCovers = Object.fromEntries(
        Object.entries(updatedCovers).filter(
          ([, cover]) => cover?.opted === true
        )
      );

      dispatch(setOptedCovers(updatedCovers));

      const getCoverSelectionSnapshot = (covers = {}) => {
        return Object.entries(covers)
          .flatMap(([coverCode, cover]) => {
            if (!cover) return [];

            if (Array.isArray(cover.value)) {
              return cover.value
                .filter((opt) => opt?.opted)
                .map(
                  (opt) => `COVER:${coverCode}:${String(opt?.name || "").trim()}`
                );
            }

            return cover?.opted ? [`COVER:${coverCode}`] : [];
          })
          .sort((a, b) => a.localeCompare(b));
      };

      const didCoverSelectionChange =
        JSON.stringify(
          getCoverSelectionSnapshot(customPlan?.addonCovers || {})
        ) !== JSON.stringify(getCoverSelectionSnapshot(updatedCovers));

      if (isHealthSupercharge && didCoverSelectionChange) {
        try {
          const selectedSIObj = siArr?.find(
            (item) => item?.id === selectedSumInsured
          );
          const selectedTenureObj = tenureArr?.find(
            (item) => item?.id === selectedTenure
          );

          const selectedTenureValue = Number(
            selectedTenureObj?.tenure || customPlan?.tenureSI?.tenure || 0
          );
          const selectedSiValue = Number(
            selectedSIObj?.si || customPlan?.tenureSI?.sumInsured || 0
          );

          const payload = {
            policyNo: renewalDetailsData?.policyNo ?? "",
            tenure: selectedTenureValue,
            sumInsured: selectedSiValue,
            fromProductCode: String(customMigOpt?.fromProductCode || ""),
            toProductCode: String(
              customPlanToProductCode ||
                renewalDetailsData?.subProductCode ||
                renewalDetailsData?.productCode ||
                ""
            ),
            migrationSchemeName: customMigOpt?.schemeName || "Current",
            packageList: Object.entries(updatedPackages)
              .filter(([, pkg]) => pkg?.isOpted)
              .map(([pkgKey, pkg]) => pkg?.name || pkgKey),
            addonCovers: updatedCovers,
          };

          const { data } = await axiosInstance.post(
            "/renewal/health/calculatePremium",
            payload
          );

          const responseData = data?.data ?? data;

          const mapped = PremiumCompute.mapCalculatedPremium(
            responseData?.premiumDtls,
            {
              schemeTypeName: responseData?.newSchemeName,
              tenure: payload.tenure,
              sumInsured: payload.sumInsured,
              isMedicareSelectPolicy,
            }
          );

          const mappedWithUpdatedCovers = {
            ...mapped,
            packages: updatedPackages,
            addonCovers: mergeAddonCoverSelectionState(
              mapped?.addonCovers,
              updatedCovers
            ),
          };

          const hasAccidentalRider = renewalDetailsData?.accidentalDeathRider;
          const hasAccidentSuraksha =
            optedPackages.includes("ACCIDENT_SURAKSHA") ||
            payload.packageList.includes("Accident Suraksha");

          const matchingScheme = premiumSchemes.find(
            (scheme) => scheme.schemeType === mappedWithUpdatedCovers.schemeTypeName
          );

          if (
            !hasAccidentalRider &&
            hasAccidentSuraksha &&
            // matchingScheme?.addonCovers?.C005?.opted &&
            mappedWithUpdatedCovers?.schemeTypeNameTn !== "exceptionas-is"
          ) {
            const c005Val = +matchingScheme?.addonCovers?.C005?.value || 0;
            const c005Gst = +matchingScheme?.addonCovers?.C005?.gstVal || 0;

            mappedWithUpdatedCovers.totalPremium = Math.round(
              (mappedWithUpdatedCovers.totalPremiumInDecimal || 0) +
                c005Val +
                c005Gst
            );
          }
          setCustomPlan(mappedWithUpdatedCovers);
          setCustomPlanState(mappedWithUpdatedCovers, selectedTenure, selectedSumInsured, mappedWithUpdatedCovers?.schemeTypeName);
          dispatch(
            checkPackagesCoversDirty({
              cardType: "customPlan",
              packages: mappedWithUpdatedCovers?.packages || {},
              covers: mappedWithUpdatedCovers?.addonCovers || {},
            })
          );

          setSelectedCard("customisedPlan");
          dispatch(
            setMigrationDetails({
              isMigrationPolicySelected: false,
            })
          );
          dispatch(setPropSelectedCard("customisedPlan"));

          setAddOnPremium(mappedWithUpdatedCovers?.totalPremium || 0);
          setAddOnScheme(mappedWithUpdatedCovers?.schemeTypeName);
          setSelectedPackagesArr(
            mappedWithUpdatedCovers?.packageList || optedPackages
          );

          return;
        } catch (error) {
          console.error(
            "calculatePremium API failed in handlePackageUpdate",
            error
          );
        }
      }

      // if (
      //   selectedCard === "migrationPlan" ||
      //   propSelectedCard === "migrationPlan"
      // ) {
      //   updateMigrationPackages(updatedPackages);
      //   return;
      // }

      const updatedPlan = PremiumCompute.compute(
        customPlan.schemeTypeName,
        optedPackages,
        MEDICARE_SELECT_CODE.includes(String(customMigOpt?.value)) ? updatedCovers : null,
        MEDICARE_SELECT_CODE.includes(String(customMigOpt?.value))
      );

      const updatedCustomPlan = {
        ...updatedPlan,
        packages: updatedPackages,
        addonCovers: updatedCovers,
      };

      const hasAccidentalRider = renewalDetailsData?.accidentalDeathRider;
      const hasAccidentSuraksha =
        optedPackages.includes("ACCIDENT_SURAKSHA") ||
        optedPackages.includes("Accident Suraksha");

      const matchingScheme = premiumSchemes.find(
        (scheme) => scheme.schemeType === updatedCustomPlan.schemeTypeName
      );

      if (
        !hasAccidentalRider &&
        hasAccidentSuraksha &&
        // matchingScheme?.addonCovers?.C005?.opted &&
        matchingScheme?.schemeTypeNameTn !== "exceptionas-is"
      ) {
        const c005Val = +matchingScheme?.addonCovers?.C005?.value || 0;
        const c005Gst = +matchingScheme?.addonCovers?.C005?.gstVal || 0;

        updatedCustomPlan.totalPremium = Math.round(
          (updatedCustomPlan.totalPremiumInDecimal || 0) + c005Val + c005Gst
        );
      }

      setCustomPlan(updatedCustomPlan);
      dispatch(
        checkPackagesCoversDirty({
          cardType: "customPlan",
          packages: updatedCustomPlan?.packages || {},
          covers: updatedCustomPlan?.addonCovers || {},
        })
      );

      setSelectedCard("customisedPlan");
      dispatch(
        setMigrationDetails({
          isMigrationPolicySelected: false,
        })
      );
      dispatch(setPropSelectedCard("customisedPlan"));

      setAddOnPremium(updatedCustomPlan.totalPremium);
      setAddOnScheme(updatedCustomPlan.schemeTypeName);
      setSelectedPackagesArr(updatedCustomPlan.packageList || []);
      if (isMedicareSelectPolicy) {
        const updatedPckCoversSnap = buildPackagesCoversSnapshot(
          updatedCustomPlan?.packages,
          updatedCustomPlan?.addonCovers
        );
        const isDirty =
          JSON.stringify(preComputedPackagesCovers.customPlan) !==
          JSON.stringify(updatedPckCoversSnap);
        if (
          isDirty
        ) {
          setShowRedirectModal(true);
        }
      }
    };

    const updateMigrationPackages = async (updatedPackages, updatedCovers = {}) => {
      const optedPackages = Object.keys(updatedPackages).filter(
        (key) => updatedPackages[key].isOpted && updatedPackages[key].value !== 0
      );

      const optedCovers = Object.fromEntries(
        Object.entries(updatedCovers).map(
          ([key, cover]) => [key, { ...cover, opted: cover?.opted === true ? true : false }]
        )
      );

      dispatch(setOptedCovers(updatedCovers));

      if (isHealthSupercharge) {
        try {
          const sel = allowedMigrationsStable.find(
            (m) => String(m.toProductCode) === String(migProduct)
          );
          const updatedPckCoversSnap = buildPackagesCoversSnapshot(
            updatedPackages,
            updatedCovers
          );

          const isDirty =
            JSON.stringify(preComputedPackagesCovers.migrationPlan) !==
            JSON.stringify(updatedPckCoversSnap);

          if (isDirty) {
            const packageList = Object.entries(updatedPackages)
              .filter(([, pkg]) => pkg?.isOpted)
              .map(([pkgKey, pkg]) => pkg?.name || pkgKey);

            const payload = {
              policyNo: renewalDetailsData?.policyNo ?? "",
              tenure: Number(migTenure),
              sumInsured: Number(migSI),
              fromProductCode: String(
                sel?.fromProductCode ?? renewalDetailsData?.productCode ?? ""
              ),
              toProductCode: String(migProduct),
              migrationSchemeName:
                sel?.schemeName !== "" ? sel?.schemeName : "Current",
              ...(migRoom ? { roomCategory: migRoom } : {}),
              packageList,
              addonCovers: optedCovers,
            };

            const { data } = await axiosInstance.post(
              "/renewal/health/calculatePremium",
              payload
            );

            const responseData = data?.data ?? data;

            const mapped = PremiumCompute.mapCalculatedPremium(
              responseData?.premiumDtls,
              {
                schemeTypeName: responseData?.newSchemeName,
                tenure: payload.tenure,
                sumInsured: payload.sumInsured,
                isMedicareSelectPolicy: isAdjustToMedicareSelect,
              }
            );
             const mappedWithUpdatedCovers = {
              ...mapped,
              packages: updatedPackages,
              addonCovers: mergeAddonCoverSelectionState(
              mapped?.addonCovers,
              updatedCovers
              ),
            };

            dispatch(
              savePreComputedPackagesCovers({
                cardType: "migrationPlan",
                packages: mappedWithUpdatedCovers?.packages || {},
                covers: mappedWithUpdatedCovers?.addonCovers || {},
              })
            );

            let isC005AddedInTotalPremium = false;
            let addedC005Value = 0;
            let addedC005GstVal = 0;
            const hasAccidentalCover = renewalDetailsData.accidentalDeathRider;
            if (
              !hasAccidentalCover &&
              // mappedWithUpdatedCovers?.ignoredAddonCovers?.C005?.opted &&
              mappedWithUpdatedCovers?.packageList?.includes("Accident Suraksha") &&
              mappedWithUpdatedCovers?.schemeTypeNameTn !== "exceptionas-is"
            ) {
              addedC005Value =
                Number(mappedWithUpdatedCovers?.ignoredAddonCovers?.C005?.value) || 0;
              addedC005GstVal =
                Number(mappedWithUpdatedCovers?.ignoredAddonCovers?.C005?.gstVal) || 0;

              mappedWithUpdatedCovers.totalPremiumInDecimal =
                Number(mappedWithUpdatedCovers.totalPremiumInDecimal || 0) +
                addedC005Value +
                addedC005GstVal;

              mappedWithUpdatedCovers.totalPremium = Math.round(mappedWithUpdatedCovers.totalPremiumInDecimal);

              isC005AddedInTotalPremium = true;
            }

            mappedWithUpdatedCovers.isC005AddedInTotalPremium = isC005AddedInTotalPremium;
            mappedWithUpdatedCovers.addedC005Value = addedC005Value;
            mappedWithUpdatedCovers.addedC005GstVal = addedC005GstVal;

            dispatch(
              setMigrationDetails({
                migrationProductCode: payload.toProductCode,
                migrationTenure: String(payload.tenure),
                migrationSI: String(payload.sumInsured),
                migrationPremium: mappedWithUpdatedCovers,
                migrationSchemeName: responseData?.newSchemeName,
                migrationFromProductCode: sel?.fromProductCode,
                migrationToProductCode: sel?.toProductCode,
                isMigrationPolicySelected: true,
              })
            );

            setSelectedCard("migrationPlan");
            dispatch(setPropSelectedCard("migrationPlan"));
            setSelectedPlan(mappedWithUpdatedCovers);
            setAddOnPremium(mappedWithUpdatedCovers.totalPremium);

            return;
          }
        } catch (error) {
          console.error(
            "calculatePremium API failed in handlePackageUpdate",
            error
          );
          return;
        }
      }

      dispatch(
        checkPackagesCoversDirty({
          cardType: "migrationPlan",
          packages: updatedPackages,
          covers: updatedCovers,
        })
      );

      const currentMigPlan = migrationPremium;
      if (!currentMigPlan) return;

      const oldPackages = currentMigPlan.packages || {};

      const getPackagesDelta = (packagesMap) =>
        Object.keys(packagesMap || {}).reduce((sum, key) => {
          const pkg = packagesMap[key];
          if (!pkg?.isOpted) return sum;

          const pkgVal = Number(pkg.value || 0);
          const pkgGst = Number(pkg.gstval || 0);

          return sum + pkgVal + pkgGst;
        }, 0);

      const getCoversDelta = (coversMap) =>
        Object.keys(coversMap || {}).reduce(
          (sum, key) => {
            if (key === "C005" || key === "C007") {
              return sum;
            }

            const cover = coversMap[key];
            if (!cover) {
              return sum;
            }

            if (Array.isArray(cover?.value)) {
              const arraySum = cover.value.reduce((acc, option) => {
                if (!option?.opted) return acc;

                const optionVal = Number(option?.value || 0);
                const optionGst = Number(
                  option?.gstval ?? option?.gstVal ?? 0
                );

                return acc + optionVal + optionGst;
              }, 0);

              return sum + arraySum;
            }

            if (!cover?.opted) {
              return sum;
            }

            const coverVal = Number(cover?.value || 0);
            const coverGst = Number(cover?.gstval ?? cover?.gstVal ?? 0);

            return sum + coverVal + coverGst;
          },
          0
        );

      const currentTotalPremiumInDecimal = Number(
        currentMigPlan.totalPremiumInDecimal ?? currentMigPlan.totalPremium ?? 0
      );

      const oldDelta = getPackagesDelta(oldPackages);
      const oldCoverDelta = getCoversDelta(currentMigPlan?.addonCovers || {});

      const previouslyAddedC005 =
        currentMigPlan?.isC005AddedInTotalPremium === true
          ? Number(currentMigPlan?.addedC005Value || 0) +
            Number(currentMigPlan?.addedC005GstVal || 0)
          : 0;

      const basePremium =
        currentTotalPremiumInDecimal - oldDelta - oldCoverDelta - previouslyAddedC005;

      const newDelta = getPackagesDelta(updatedPackages);
      const newCoverDelta = getCoversDelta(updatedCovers);

      let newTotalPremiumInDecimal = basePremium + newDelta + newCoverDelta;

      const newPackageList = optedPackages.map(
        (key) => updatedPackages[key]?.name || key
      );

      const riders = [];
      optedPackages.forEach((key) => {
        const pkg = updatedPackages[key];
        if (Array.isArray(pkg?.riders)) {
          riders.push(...pkg.riders);
        }
      });

      const hasAccidentalRider = renewalDetailsData?.accidentalDeathRider;
      const hasAccidentSuraksha =
        optedPackages.includes("ACCIDENT_SURAKSHA") ||
        newPackageList.includes("Accident Suraksha");

      let addedC005Value = 0;
      let addedC005GstVal = 0;
      let isC005AddedInTotalPremium = false;

      if (
        !hasAccidentalRider &&
        hasAccidentSuraksha &&
        // currentMigPlan?.ignoredAddonCovers?.C005?.opted &&
        currentMigPlan?.schemeTypeNameTn !== "exceptionas-is"
      ) {
        addedC005Value = Number(
          currentMigPlan?.ignoredAddonCovers?.C005?.value || 0
        );
        addedC005GstVal = Number(
          currentMigPlan?.ignoredAddonCovers?.C005?.gstVal || 0
        );

        newTotalPremiumInDecimal =
          newTotalPremiumInDecimal + addedC005Value + addedC005GstVal;

        isC005AddedInTotalPremium = true;
      }

      const newTotalPremium = Math.round(newTotalPremiumInDecimal);

      const newMigrationPlan = {
        ...currentMigPlan,
        totalPremium: newTotalPremium,
        totalPremiumInDecimal: newTotalPremiumInDecimal,
        addonCoverPremiumDeltaWithGST: Math.round(newCoverDelta),
        addonCoverPremiumDeltaBreakup: {
          premiumInDecimal: Number(newCoverDelta || 0),
          premium: Math.round(Number(newCoverDelta || 0)),
          gstInDecimal: 0,
          gst: 0,
        },
        packagePremiumWithGST: newDelta,
        packagePremiumBreakup: {
          premiumInDecimal: newDelta,
          premium: Math.round(newDelta),
          gstInDecimal: 0,
          gst: 0,
        },
        packageCount: optedPackages.length,
        packageList: newPackageList,
        packages: updatedPackages,
        addonCovers: updatedCovers,
        riders,
        isC005AddedInTotalPremium,
        addedC005Value,
        addedC005GstVal,
      };

      setSelectedCard("migrationPlan");
      dispatch(setPropSelectedCard("migrationPlan"));

      dispatch(
        setMigrationDetails({
          ...migrationDetails,
          migrationPremium: newMigrationPlan,
        })
      );

      setSelectedPlan(newMigrationPlan);
      setAddOnPremium(newMigrationPlan.totalPremium);
      setSelectedPackagesArr(newMigrationPlan.packageList || []);
      if (isMedicareSelectPolicy) {
        const updatedPckCoversSnap = buildPackagesCoversSnapshot(
          newMigrationPlan?.packages,
          newMigrationPlan?.addonCovers
        );
        const isDirty =
          JSON.stringify(preComputedPackagesCovers.migrationPlan) !==
          JSON.stringify(updatedPckCoversSnap);
        if (
          isDirty &&
          (globalUserFlag || renewalDetailsData?.isGlobalSuperUser)
        ) {
          setShowRedirectModal(true);
        }
      }
    };

    const handleLottieComplete = () => {
      setViewCustomCard(true);
    };

    useEffect(() => {
      if (ref) {
        ref.current = {
          handleCardSelection,
        };
      }
    }, [ref, handleCardSelection]);

    const getFooterInfoText = (cardType) => {
      const expiringDateStr = PRICE_REVISION_CUTOFF_DATE.JULY_POST;
      const expiryDate = new Date(expiringDateStr);
      let isBeforeOrOn27July = false;
      let isPostJuly27 = false;

      if (expiringDateStr) {
        const date = new Date(expiringDateStr);
        const month = date.getMonth();
        const day = date.getDate();
        isBeforeOrOn27July = month < 6 || (month === 6 && day <= 27);
        const cutoffDate = new Date(PRICE_REVISION_CUTOFF_DATE.JULY);
        isPostJuly27 = expiryDate > cutoffDate;
      }

      if (globalUserFlag && isBeforeOrOn27July && isNewUIN) {
        if (cardType === "current") return "Base Plan with Old Rates";
        if (cardType === "recommended") return "Base Plan with New Rates";
        if (cardType === "custom") return "Customized Plan";
      } else if (isPostJuly27) {
        if (cardType === "current") return "Your Base Plan";
        if (cardType === "recommended") return "A Suggested Plan for You!";
        if (cardType === "custom") return "A Customized Plan for You!";
      }
    };

    const shouldShowCustomPlan = () =>
      !!customPlan &&
      !!currentPlan &&
      (customPlan.isMigrationPolicy ||
        tenureArr.length > 1 ||
        siArr.length > 1 ||
        (customPlan.packages && Object.keys(customPlan.packages).length > 0) ||
        (isHealthSupercharge &&
          customPlan?.addonCovers &&
          Object.keys(customPlan.addonCovers).length > 0));

    const mandatoryPackageOptOutFlag = () => globalUserFlag;

    useEffect(() => {
      if (renewalDetailsData?.autoDebitProcessing) {
        setShowModal(true);
      }
    }, [renewalDetailsData?.autoDebitProcessing]);

    const migrationEligible = !!renewalDetailsData?.isGlobalSuperUser;

    const hasMigrationPremium = useMemo(() => {
      return (
        migrationPremium &&
        typeof migrationPremium === "object" &&
        !Array.isArray(migrationPremium) &&
        Object.keys(migrationPremium).length > 0
      );
    }, [migrationPremium]);
    const showCalculate = migDirty || !hasMigrationPremium;
    const recommendedVisible = !!recommendedPlan && !migrationEligible;

    const handlePickMigrateToForCustom = (val) => {
      userEditedCustomPlanRef.current = true;
      const opt = migrateToOptions.find((o) => o.value === val) || null;

      setCustomMigOpt(opt);

      if (opt?.plan) {
        const planClone = JSON.parse(JSON.stringify(opt.plan));
        const pkgMatch = premiumSchemes.find(
          (s) => s.schemeType === planClone.schemeTypeName
        );
        if (pkgMatch) planClone.packages = pkgMatch.packages || {};
        setCustomPlan(planClone);
        if (planClone) {
          dispatch(
            savePreComputedPackagesCovers({
              cardType: "customPlan",
              packages: planClone?.packages || {},
              covers: planClone?.addonCovers || {},
            })
          );
        }

        setAddOnScheme(planClone.schemeTypeName);
        setAddOnPremium(planClone.totalPremium);
        setSelectedPackagesArr(planClone.packageList || []);
        setSelectedCard("customisedPlan");
        dispatch(setPropSelectedCard("customisedPlan"));
      } else {
        setCustomMigOpt(null);
        const init = PremiumCompute.getInitMapping();
        const sortedTenure = [...init.tenureList].sort(
          (a, b) => a.value - b.value
        );
        const sortedSi = [...init.siList].sort((a, b) => a.value - b.value);
        const t0 = sortedTenure[0]?.id;
        const si0 = sortedSi[0]?.id;
        setTenureArr(sortedTenure);
        setSiArr(sortedSi);
        setSelectedTenure(t0);
        setSelectedSumInsured(si0);
        updateCustomPlan(t0, si0, init.tenureSiSchemeMap);
      }
    };

    const shouldShowProductDropdown = useMemo(() => {
      const allowed =
        renewalDetailsData?.productMigrationData?.allowedMigrations || [];
      if (!allowed.length) return false;

      const firstAllowed = allowed[0];

      // Rule 1: Global Super User
      if (renewalDetailsData?.isGlobalSuperUser) return true;

      // Rule 2: Global User + Migration permission
      if (globalUserFlag && firstAllowed?.isGlobalUserAllowed) return true;

      // Rule 3: Producer role + allowed
      if (!isCustomer && firstAllowed?.isProducerAllowed) return true;

      return false;
    }, [
      renewalDetailsData?.isGlobalSuperUser,
      globalUserFlag,
      isCustomer,
      renewalDetailsData?.productMigrationData?.allowedMigrations,
    ]);
    const customPlanToProductCode =
      customMigOpt === null
        ? renewalDetailsData?.subProductCode
        : customMigOpt?.toProductCode ||
        customMigOpt?.value ||
        migrationDetails?.migrationToProductCode ||
        renewalDetailsData?.subProductCode;
    const migrationPlanToProductCode = migrationDetails?.isMigrationPolicy ?
      migrationDetails?.migrationProductCode :
      renewalDetailsData?.subProductCode;
    useLayoutEffect(() => {
      if (!initialSelectionSetRef.current) {
        initialSelectionSetRef.current = true;
        if (!propSelectedCard || propSelectedCard === "recommendedPlan") {
          setSelectedCard("currentPlan");
          dispatch(setPropSelectedCard("currentPlan"));
        } else {
          setSelectedCard(propSelectedCard);
        }
        return;
      }

      if (migrationEligible && propSelectedCard === "recommendedPlan") {
        setSelectedCard("currentPlan");
        dispatch(setPropSelectedCard("currentPlan"));
      }
    }, [dispatch, migrationEligible, propSelectedCard]);
    useEffect(() => {
      if (propSelectedCard === "currentPlan" && currentPlan) {
        setAddOnPremium(currentPlan?.totalPremium);
      }
    }, [currentPlan, propSelectedCard]);
    const hasOptionalPackagesMig = React.useMemo(
      () =>
        !migrationPremium?.isMigrationPolicy &&
        !!migrationPremium?.packages &&
        Object.values(migrationPremium.packages).some(
          (pkg) => !pkg?.isMandatory
        ),
      [migrationPremium?.isMigrationPolicy, migrationPremium?.packages]
    );

    useEffect(() => {
      const resolvedSelectedCard =
        effectiveSelected ||
        (renewalDetailsData?.isGlobalSuperUser
          ? "currentPlan"
          : recommendedPlan
          ? "recommendedPlan"
          : "currentPlan");

      const selectedPlanCovers =
        {
          currentPlan: currentPlan?.addonCovers,
          recommendedPlan: recommendedPlan?.addonCovers,
          customisedPlan: customPlan?.addonCovers,
          migrationPlan: migrationPremium?.addonCovers,
        }[resolvedSelectedCard] || {};

      dispatch(setOptedCovers(selectedPlanCovers));
    }, [
      dispatch,
      effectiveSelected,
      currentPlan?.addonCovers,
      recommendedPlan?.addonCovers,
      customPlan?.addonCovers,
      migrationPremium?.addonCovers,
      renewalDetailsData?.isGlobalSuperUser,
      recommendedPlan,
    ]);

    const shouldShowPremium = (plan = {}, cardType = "customPlan") => {
      const updatedPckCoversSnap = buildPackagesCoversSnapshot(
        plan?.packages,
        plan?.addonCovers
      );
      const isDirty =
        JSON.stringify(preComputedPackagesCovers[cardType] || []) !==
        JSON.stringify(updatedPckCoversSnap);
      return !(
        (globalUserFlag || renewalDetailsData?.isGlobalSuperUser) &&
        isMedicareSelectPolicy &&
        isDirty
      );
    };

    return (
      <>
        <div className="upsellPolicyContainer pt-5">
          <div className="row">
            {/* Current Plan */}
            {currentPlan && packages && (
              <div className="tata-card-container col-md-6 col-lg-4">
                <input
                  type="radio"
                  id="currentPlan"
                  name="UpsellPlan"
                  value="currentPlan"
                  checked={effectiveSelected === "currentPlan"}
                  onChange={(e) =>
                    handleCardSelection(
                      e.target.value,
                      currentPlan,
                      renewalDetailsData
                    )
                  }
                />
                <label
                  className={classNames("tata-card", {
                    "tata-card-selected": effectiveSelected === "currentPlan",
                  })}
                  htmlFor="currentPlan"
                >
                  <div className="tata-card-addon">
                    <div className="header-title">
                      <span>Current Plan</span>
                    </div>
                  </div>
                  <div className="tata-card-body tata-card-custom">
                    <div className="addon-info-section">
                      <label className="theme-value">
                        {t("tenure")}
                        <span className="theme-label">
                          {currentPlan.tenureSI.tenure} Years
                        </span>
                      </label>
                      <label className="theme-value">
                        {t("sum_insured_titlecase")}
                        <span className="theme-label">
                          ₹ {currentPlan.tenureSI.shortSumInsured} Lakhs
                        </span>
                      </label>
                      {isHealthSupercharge
                        ? (() => {
                            const optedCoverEntries = Object.entries(
                              currentPlan?.addonCovers || {}
                            ).filter(([, cover]) => isCoverVisible(cover));
                            if (!optedCoverEntries.length) return null;
                            return (
                              <label className="theme-value packages-info">
                                Covers ({optedCoverEntries.length})
                                <span className="theme-label package-list">
                                  {optedCoverEntries
                                    .slice(0, 3)
                                    .map(([coverCode, cover]) => (
                                      <span
                                        key={coverCode}
                                        className="package-item"
                                      >
                                        <span className="package-icon">
                                          <Image
                                            src={`${IMAGE_BASE_PATH}/images/Green_ticks.svg`}
                                            alt="Checked"
                                            width={16}
                                            height={16}
                                          />
                                        </span>
                                        {cover?.name || coverCode}
                                      </span>
                                    ))}
                                  {optedCoverEntries.length > 3 && (
                                    <span className="package-more">... </span>
                                  )}
                                </span>
                                <span className="view-link">
                                  <PackageCanvasMenu
                                    packages={packages || {}}
                                    userCanOptOutMandatory={true}
                                    readOnly={true}
                                    addonCovers={addonCovers}
                                    optedCovers={currentPlan?.addonCovers}
                                    subProductCode={
                                      renewalDetailsData?.subProductCode ||
                                      renewalDetailsData?.productCode
                                    }
                                  />
                                </span>
                              </label>
                            );
                          })()
                        : currentPlan.packageCount > 0 && (
                            <label className="theme-value packages-info">
                              Packages ({currentPlan.packageCount})
                              <span className="theme-label package-list">
                                {getSafePackageList(currentPlan)
                                  .slice(0, 3)
                                  .map((pkg, index) => (
                                    <span key={index} className="package-item">
                                      <span className="package-icon">
                                        <Image
                                          src={`${IMAGE_BASE_PATH}/images/Green_ticks.svg`}
                                          alt="Checked"
                                          width={16}
                                          height={16}
                                        />
                                      </span>
                                      {pkg.replace(/_/g, " ")}
                                    </span>
                                  ))}
                                {getSafePackageList(currentPlan).length > 3 && (
                                  <span className="package-more">... </span>
                                )}
                              </span>
                              <span className="view-link">
                                <PackageCanvasMenu
                                  packages={packages}
                                  userCanOptOutMandatory={true}
                                  readOnly={true}
                                  addonCovers={addonCovers}
                                  optedCovers={currentPlan?.addonCovers}
                                  subProductCode={
                                    renewalDetailsData?.subProductCode
                                  }
                                />
                              </span>
                            </label>
                          )}
                    </div>

                    <div className="premium-section ">
                      <span className="premium-value">
                        <div className="px-1">
                          <label
                            className={classNames("p-0 mb-1", premiumStyle)}
                          >
                            {t("as_low_as")}
                          </label>
                        </div>
                        {displayPremiumAmount(currentPlan.totalPremium)}
                      </span>
                      <article
                        className={classNames(
                          "detail-container mt-2",
                          mAlignCenter
                        )}
                      >
                        <div>
                          <button
                            type="button"
                            className="link-btn"
                            onClick={() => openSelectedSchemeModal(currentPlan)}
                          >
                            View Premium Breakup
                          </button>
                        </div>
                      </article>
                    </div>
                  </div>
                  <div
                    className={classNames("tata-card-footer different-msg", {
                      "tata-card-selected-footer":
                        effectiveSelected === "currentPlan",
                    })}
                  >
                    {isCustomer ? (
                      <span className="footer-info">
                        Your Base Plan
                        {isCustomer && currentPlan?.packageCount > 0 && (
                          <> including Packages.</>
                        )}
                      </span>
                    ) : (
                      <span className="footer-info">
                        {getFooterInfoText("current")}
                      </span>
                    )}
                  </div>
                </label>
              </div>
            )}

            {/* Recommended Plan */}
            {recommendedVisible && (
              <div className="tata-card-container col-md-6 col-lg-4">
                <input
                  type="radio"
                  id="recommendedPlan"
                  name="UpsellPlan"
                  value="recommendedPlan"
                  checked={effectiveSelected === "recommendedPlan"}
                  onChange={(e) =>
                    handleCardSelection(
                      e.target.value,
                      recommendedPlan,
                      renewalDetailsData
                    )
                  }
                />
                <label
                  className={classNames("tata-card", {
                    "tata-card-selected":
                      effectiveSelected === "recommendedPlan",
                  })}
                  htmlFor="recommendedPlan"
                >
                  <div className="tata-card-addon tata-card-default">
                    <div className="header-title">
                      <span className="header-img">
                        <Image
                          src={`${IMAGE_BASE_PATH}/images/Recommend_icon.svg`}
                          alt="X"
                          width={20}
                          height={20}
                        />
                      </span>
                      Recommended Plan
                    </div>
                  </div>
                  <div className="tata-card-body tata-card-custom">
                    <div className="addon-info-section">
                      <label className="theme-value">
                        {t("tenure")}
                        <span className="theme-label">
                          {recommendedPlan.tenureSI.tenure} Years
                        </span>
                      </label>
                      <label className="theme-value">
                        {t("sum_insured_titlecase")}
                        <span className="theme-label">
                          ₹ {recommendedPlan.tenureSI.shortSumInsured} Lakhs
                        </span>
                      </label>
                      {isHealthSupercharge
                        ? (() => {
                            const optedCoverEntries = Object.entries(
                              recommendedPlan?.addonCovers || {}
                            ).filter(([, cover]) => isCoverVisible(cover));
                            if (!optedCoverEntries.length) return null;
                            return (
                              <label className="theme-value packages-info">
                                Covers ({optedCoverEntries.length})
                                <span className="theme-label package-list">
                                  {optedCoverEntries
                                    .slice(0, 3)
                                    .map(([coverCode, cover]) => (
                                      <span
                                        key={coverCode}
                                        className="package-item"
                                      >
                                        <span className="package-icon">
                                          <Image
                                            src={`${IMAGE_BASE_PATH}/images/Green_ticks.svg`}
                                            alt="Checked"
                                            width={16}
                                            height={16}
                                          />
                                        </span>
                                        {cover?.name || coverCode}
                                      </span>
                                    ))}
                                  {optedCoverEntries.length > 3 && (
                                    <span className="package-more">... </span>
                                  )}
                                </span>
                                <span className="view-link">
                                  <PackageCanvasMenu
                                    packages={recommendedPlan.packages || {}}
                                    userCanOptOutMandatory={true}
                                    readOnly={true}
                                    addonCovers={addonCovers}
                                    optedCovers={recommendedPlan?.addonCovers}
                                    subProductCode={
                                      renewalDetailsData?.subProductCode ||
                                      renewalDetailsData?.productCode
                                    }
                                  />
                                </span>
                              </label>
                            );
                          })()
                        : recommendedPlan.packageCount > 0 && (
                            <label className="theme-value packages-info">
                              Packages ({recommendedPlan.packageCount})
                              <span className="theme-label package-list">
                                {getSafePackageList(recommendedPlan)
                                  .slice(0, 3)
                                  .map((pkg, index) => (
                                    <span key={index} className="package-item">
                                      <span className="package-icon">
                                        <Image
                                          src={`${IMAGE_BASE_PATH}/images/Green_ticks.svg`}
                                          alt="Checked"
                                          width={16}
                                          height={16}
                                        />
                                      </span>
                                      {pkg.replace(/_/g, " ")}
                                    </span>
                                  ))}
                                {getSafePackageList(recommendedPlan).length >
                                  3 && (
                                  <span className="package-more">... </span>
                                )}
                              </span>
                              <span className="view-link">
                                <PackageCanvasMenu
                                  packages={recommendedPlan.packages}
                                  userCanOptOutMandatory={true}
                                  readOnly={true}
                                  addonCovers={addonCovers}
                                  optedCovers={recommendedPlan?.addonCovers}
                                  subProductCode={
                                    renewalDetailsData?.subProductCode
                                  }
                                />
                              </span>
                            </label>
                          )}
                    </div>

                    <div className="premium-section ">
                      <span className="premium-value">
                        <div className="px-1">
                          <label
                            className={classNames("p-0 mb-1", premiumStyle)}
                          >
                            {t("as_low_as")}
                          </label>
                        </div>
                        {displayPremiumAmount(
                          recommendedPlan.totalPremium,
                          recommendedPlan.tenureSI.tenure
                        )}
                      </span>
                      <article
                        className={classNames(
                          "detail-container mt-2",
                          mAlignCenter
                        )}
                      >
                        <div>
                          <button
                            type="button"
                            className="link-btn"
                            onClick={() =>
                              openSelectedSchemeModal(recommendedPlan)
                            }
                          >
                            View Premium Breakup
                          </button>
                        </div>
                      </article>
                    </div>
                  </div>

                  {!isCustomer ? (
                    <div
                      className={classNames("tata-card-footer different-msg", {
                        "tata-card-selected-footer":
                          effectiveSelected === "recommendedPlan",
                      })}
                    >
                      <span className="footer-info">
                        {getFooterInfoText("recommended")}
                      </span>
                    </div>
                  ) : (
                    <div
                      className={classNames("tata-card-footer different-msg", {
                        "tata-card-selected-footer":
                          effectiveSelected === "recommendedPlan",
                      })}
                    >
                      {recommendedPlan?.benefits ? (
                        <span className="footer-info">
                          {recommendedPlan.benefits.savePercentage > 0 ? (
                            <>
                              Save {recommendedPlan.benefits.savePercentage}%
                              {recommendedPlan.benefits.additionalSI !== 0 &&
                              recommendedPlan.packageCount > 0 ? (
                                <>
                                  , Additional ₹{" "}
                                  {recommendedPlan.benefits.additionalSI /
                                    100000}
                                  L Coverage including Packages.
                                </>
                              ) : recommendedPlan.benefits.additionalSI !==
                                0 ? (
                                <>
                                  {" "}
                                  & Additional ₹{" "}
                                  {recommendedPlan.benefits.additionalSI /
                                    100000}
                                  L Coverage.
                                </>
                              ) : recommendedPlan.packageCount > 0 ? (
                                <> including Packages.</>
                              ) : null}
                            </>
                          ) : recommendedPlan.benefits.additionalSI !== 0 ? (
                            <>
                              Additional ₹{" "}
                              {recommendedPlan.benefits.additionalSI / 100000}L
                              Coverage
                              {recommendedPlan.packageCount > 0 && (
                                <> including Packages</>
                              )}
                              .
                            </>
                          ) : (
                            recommendedPlan.packageCount > 0 && (
                              <>Including Packages.</>
                            )
                          )}
                        </span>
                      ) : (
                        <span className="footer-info">
                          Your Base Plan
                          {isCustomer && currentPlan?.packageCount > 0 && (
                            <> including Packages.</>
                          )}
                        </span>
                      )}
                    </div>
                  )}
                </label>
              </div>
            )}

            {/* Custom Plan */}
            {shouldShowCustomPlan() && (
              <div className="tata-card-container col-md-6 col-lg-4">
                <input
                  type="radio"
                  id="customisedPlan"
                  name="UpsellPlan"
                  value="customisedPlan"
                  checked={effectiveSelected === "customisedPlan"}
                  onChange={(e) =>
                    handleCardSelection(
                      e.target.value,
                      customPlan,
                      renewalDetailsData
                    )
                  }
                />
                <label
                  className={classNames("tata-card", {
                    "tata-card-selected":
                      effectiveSelected === "customisedPlan",
                  })}
                  htmlFor="customisedPlan"
                >
                  <div className="tata-card-addon">
                    <div className="header-title">
                      Customise Your Current Plan
                    </div>
                  </div>
                  <div className="tata-card-body tata-card-custom">
                    <div className="addon-info-section dropdown-only">
                      {shouldShowProductDropdown &&
                        migrateToOptions.length > 0 && (
                          <div className="field-row mb-3">
                            <label htmlFor="customMigTo" className="neo-label">
                              Product:
                            </label>
                            <NeoSelect
                              id="customMigTo"
                              value={
                                customMigOpt?.value ??
                                migrateToOptions[0]?.value ??
                                ""
                              }
                              onChange={handlePickMigrateToForCustom}
                              placeholder="Select Product"
                              options={migrateToOptions.map((o) => ({
                                value: o.value,
                                label: o.label,
                              }))}
                            />
                          </div>
                        )}
                      {customMigOpt && (
                        <>
                          <label className="theme-value">
                            {t("tenure")}
                            <span className="theme-label">
                              {customPlan.tenureSI.tenure} Years
                            </span>
                          </label>
                          <label className="theme-value">
                            {t("sum_insured_titlecase")}
                            <span className="theme-label">
                              ₹ {customPlan.tenureSI.shortSumInsured} Lakhs
                            </span>
                          </label>
                        </>
                      )}
                    </div>

                    <div
                      className={classNames("customise-section", {
                        "optional-section": Object.values(
                          customPlan.packages || {}
                        ).some((pkg) => !pkg?.isMandatory),
                        "migration-section": !!customMigOpt,
                      })}
                    >
                      {!customMigOpt && (
                        <>
                          <div className="tenure-section">
                            <label>Tenure(Yr):</label>
                            <div className="options">
                              {tenureArr
                                .filter(
                                  (tenure) =>
                                    Number(tenure?.value || tenure?.tenure) > 0
                                )
                                .map((tenure) => (
                                  <React.Fragment key={tenure.id}>
                                    <input
                                      type="radio"
                                      id={tenure.id}
                                      name="tenure"
                                      value={tenure.id}
                                      disabled={tenure.disabled}
                                      checked={selectedTenure === tenure.id}
                                      onChange={() =>
                                        handleTenureChange(tenure.id)
                                      }
                                    />
                                    <label
                                      htmlFor={tenure.id}
                                      className={`option-label ${
                                        selectedTenure === tenure.id
                                          ? "selected"
                                          : ""
                                      }`}
                                    >
                                      {tenure.tenure}
                                    </label>
                                  </React.Fragment>
                                ))}
                            </div>
                          </div>
                          <div className="sum-insured-section">
                            <label>Sum Insured (Lakh):</label>
                            <div className="options">
                              {siArr
                                .filter((si) => Number(si?.value) > 0)
                                .map((si) => (
                                  <React.Fragment key={si.id}>
                                    <input
                                      type="radio"
                                      id={si.id}
                                      name="sumInsured"
                                      value={si.id}
                                      disabled={si.disabled}
                                      checked={selectedSumInsured === si.id}
                                      onChange={() =>
                                        handleSumInsuredChange(si.id)
                                      }
                                    />
                                    <label
                                      htmlFor={si.id}
                                      className={`option-label ${
                                        selectedSumInsured === si.id
                                          ? "selected"
                                          : ""
                                      }`}
                                    >
                                      {si.value}
                                    </label>
                                  </React.Fragment>
                                ))}
                            </div>
                          </div>
                        </>
                      )}

                      {!isHealthSupercharge &&
                        (Object.keys(customPlan.packages || {}).length > 0 ||
                          (Object.keys(customPlan.addonCovers || {}).length >
                            0 &&
                            (PACKAGES_COVERS_ENABLED_SUBPRODUCT_CODES.has(
                              String(
                                renewalDetailsData?.subProductCode ||
                                  renewalDetailsData?.productCode ||
                                  ""
                              )
                            ) ||
                              PACKAGES_COVERS_ENABLED_SUBPRODUCT_CODES.has(
                                String(customPlanToProductCode || "")
                              )))) && (
                          <>
                            <label className="theme-value packages-info">
                              Packages ({customPlan.packageCount})
                              <span className="theme-label package-list">
                                {getSafePackageList(customPlan)
                                  .slice(0, 3)
                                  .map((pkg, index) => (
                                    <span key={index} className="package-item">
                                      <span className="package-icon">
                                        <Image
                                          src={`${IMAGE_BASE_PATH}/images/Green_ticks.svg`}
                                          alt="Checked"
                                          width={16}
                                          height={16}
                                        />
                                      </span>
                                      {pkg.replace(/_/g, " ")}
                                    </span>
                                  ))}
                                {getSafePackageList(customPlan).length > 3 && (
                                  <span className="package-more">... </span>
                                )}
                              </span>
                              <span className="view-link">
                                <PackageCanvasMenu
                                  toProductCode={customPlanToProductCode}
                                  packages={customPlan?.packages}
                                  userCanOptOutMandatory={mandatoryPackageOptOutFlag()}
                                  readOnly={false}
                                  onUpdate={handlePackageUpdate}
                                  addonCovers={addonCovers}
                                  packageCount={customPlan?.packageCount}
                                  optedCovers={customPlan?.addonCovers}
                                  subProductCode={
                                    renewalDetailsData?.subProductCode ||
                                    renewalDetailsData?.productCode
                                  }
                                />
                              </span>
                            </label>
                            {Object.values(customPlan.packages).some(
                              (pkg) => !pkg?.isMandatory
                            ) && (
                              <div className="optional-chip-wrapper">
                                <div className="optional-chip">
                                  Optional Packages Available
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      {isHealthSupercharge &&
                        (Object.keys(customPlan.packages || {}).length > 0 ||
                          (Object.keys(customPlan.addonCovers || {}).length >
                            0 &&
                            (PACKAGES_COVERS_ENABLED_SUBPRODUCT_CODES.has(
                              String(
                                renewalDetailsData?.subProductCode ||
                                  renewalDetailsData?.productCode ||
                                  ""
                              )
                            ) ||
                              PACKAGES_COVERS_ENABLED_SUBPRODUCT_CODES.has(
                                String(customPlanToProductCode || "")
                              )))) && (
                          <>
                            <label className="theme-value packages-info">
                              Covers (
                              {
                                Object.entries(
                                  customPlan?.addonCovers || {}
                                ).filter(([, cover]) => isCoverVisible(cover))
                                  .length
                              }
                              )
                              <span className="theme-label package-list">
                                {Object.entries(customPlan?.addonCovers || {})
                                  .filter(([, cover]) => isCoverVisible(cover))
                                  .slice(0, 3)
                                  .map(([coverCode, cover]) => (
                                    <span
                                      key={coverCode}
                                      className="package-item"
                                    >
                                      <span className="package-icon">
                                        <Image
                                          src={`${IMAGE_BASE_PATH}/images/Green_ticks.svg`}
                                          alt="Checked"
                                          width={16}
                                          height={16}
                                        />
                                      </span>
                                      {cover?.name || coverCode}
                                    </span>
                                  ))}
                                {Object.entries(
                                  customPlan?.addonCovers || {}
                                ).filter(([, cover]) => isCoverVisible(cover))
                                  .length > 3 && (
                                  <span className="package-more">... </span>
                                )}
                              </span>
                              <span className="view-link">
                                <PackageCanvasMenu
                                  toProductCode={customPlanToProductCode}
                                  packages={customPlan?.packages}
                                  userCanOptOutMandatory={mandatoryPackageOptOutFlag()}
                                  readOnly={false}
                                  onUpdate={handlePackageUpdate}
                                  addonCovers={addonCovers}
                                  packageCount={
                                    Object.entries(
                                      customPlan?.addonCovers || {}
                                    ).filter(([, cover]) =>
                                      isCoverVisible(cover)
                                    ).length
                                  }
                                  optedCovers={customPlan?.addonCovers}
                                  subProductCode={
                                    renewalDetailsData?.subProductCode ||
                                    renewalDetailsData?.productCode
                                  }
                                />
                              </span>
                            </label>
                            {Object.values(customPlan.packages).some(
                              (pkg) => !pkg?.isMandatory
                            ) && (
                              <div className="optional-chip-wrapper">
                                <div className="optional-chip">
                                  Optional Packages Available
                                </div>
                              </div>
                            )}
                          </>
                        )}
                    </div>

                    <div className="premium-section ">
                      {shouldShowPremium(customPlan, "customPlan") && (
                        <span className="premium-value">
                          <div className="px-1">
                            <label
                              className={classNames("p-0 mb-1", premiumStyle)}
                            >
                              {t("as_low_as")}
                            </label>
                          </div>
                          {displayPremiumAmount(
                            customPlan.totalPremium,
                            customPlan.tenureSI.tenure
                          )}
                        </span>
                      )}
                      <article
                        className={classNames(
                          "detail-container mt-2",
                          mAlignCenter
                        )}
                      >
                        <div>
                          <button
                            type="button"
                            className="link-btn"
                            onClick={() => openSelectedSchemeModal(customPlan)}
                          >
                            View Premium Breakup
                          </button>
                        </div>
                      </article>
                    </div>
                  </div>
                  {customPlan.benefits &&
                  (customPlan.benefits.additionalSI !== 0 ||
                    customPlan.benefits.savePercentage !== 0) ? (
                    <div
                      className={classNames("tata-card-footer different-msg", {
                        "tata-card-selected-footer":
                          effectiveSelected === "customisedPlan",
                      })}
                    >
                      <span className="footer-info">
                        {customPlan.benefits.savePercentage > 0 ? (
                          <>
                            Save {customPlan.benefits.savePercentage}%
                            {customPlan.benefits.additionalSI !== 0 &&
                            customPlan.packageCount > 0 ? (
                              <>
                                , Additional ₹{" "}
                                {customPlan.benefits.additionalSI / 100000}L
                                Coverage including Packages.
                              </>
                            ) : customPlan.benefits.additionalSI !== 0 ? (
                              <>
                                {" "}
                                & Additional ₹{" "}
                                {customPlan.benefits.additionalSI / 100000}L
                                Coverage.
                              </>
                            ) : customPlan.packageCount > 0 ? (
                              <> including Packages.</>
                            ) : null}
                          </>
                        ) : customPlan.benefits.additionalSI !== 0 ? (
                          <>
                            Additional ₹{" "}
                            {customPlan.benefits.additionalSI / 100000}L
                            Coverage
                            {customPlan.packageCount > 0 && (
                              <> including Packages</>
                            )}
                            .
                          </>
                        ) : (
                          customPlan.packageCount > 0 && (
                            <>Including Packages.</>
                          )
                        )}
                      </span>
                    </div>
                  ) : (
                    <div
                      className={classNames("tata-card-footer different-msg", {
                        "tata-card-selected-footer":
                          effectiveSelected === "customisedPlan",
                      })}
                    >
                      {isCustomer ? (
                        <span className="footer-info">
                          Your Custom Plan
                          {customPlan.packageCount > 0 && (
                            <> including Packages</>
                          )}
                          .
                        </span>
                      ) : (
                        <span className="footer-info">
                          {getFooterInfoText("custom")}
                        </span>
                      )}
                    </div>
                  )}
                </label>
              </div>
            )}

            {/* Migration Card */}
            {migrationEligible && (
              <div className="tata-card-container col-md-6 col-lg-4">
                <input
                  type="radio"
                  id="migrationPlan"
                  name="UpsellPlan"
                  value="migrationPlan"
                  checked={effectiveSelected === "migrationPlan"}
                  onChange={(e) =>
                    handleCardSelection(
                      e.target.value,
                      migrationPremium,
                      renewalDetailsData
                    )
                  }
                />

                <label
                  className={classNames("tata-card", {
                    "tata-card-selected": effectiveSelected === "migrationPlan",
                  })}
                  htmlFor="migrationPlan"
                >
                  <div className="tata-card-addon tata-card-default">
                    <div className="header-title">Adjust Your Plan</div>
                  </div>

                  <div className="tata-card-body tata-card-custom">
                    <div className="migration-section dropdown-only">
                      {/* Migrate To */}
                      <div className="field-row">
                        <label htmlFor="migProduct" className="neo-label">
                          Migrate To:
                        </label>
                        <NeoSelect
                          id="migProduct"
                          value={migProduct}
                          onChange={(val) => {
                            setMigProduct(val);
                            setMigTenure("");
                            setMigSI("");
                            setMigDirty(true);
                            dispatch(
                              setMigrationDetails({
                                migrationProductCode: val,
                                migrationTenure: "",
                                migrationSI: "",
                                migrationPremium: "",
                              })
                            );
                            handleMigrationChange({ target: { value: val } });
                          }}
                          placeholder="Select Product"
                          options={allowedMigrationsStable.map((m) => ({
                            value: m.toProductCode,
                            label: m.toProductName,
                          }))}
                        />
                      </div>

                      {/* Tenure */}
                      <div className="field-row">
                        <label htmlFor="migTenure" className="neo-label">
                          Tenure (Yr):
                        </label>
                        <div
                          className={classNames({
                            "neo-sel-wrapper-disabled": migrationTenureDisabled,
                          })}
                        >
                          <NeoSelect
                            id="migTenure"
                            value={migTenure}
                            onChange={(val) => {
                              setMigTenure(val);
                              setMigDirty(true);
                              dispatch(
                                setMigrationDetails({
                                  migrationProductCode: migProduct,
                                  migrationTenure: val,
                                  migrationSI: migSI || "",
                                  migrationPremium: "",
                                })
                              );
                            }}
                            placeholder="Select Tenure"
                            options={(isMedicareSelectRevision
                              ? [1, 2, 3, 4, 5]
                              : [1, 2, 3]
                            ).map((yr) => ({
                              value: String(yr),
                              label: `${yr} ${yr > 1 ? "Years" : "Year"}`,
                            }))}
                            disabled={migrationTenureDisabled}
                          />
                        </div>
                      </div>

                      {/* Sum Insured */}
                      <div className="field-row">
                        <label htmlFor="migSi" className="neo-label">
                          Sum Insured (Lakh):
                        </label>
                        <div
                          className={classNames({
                            "neo-sel-wrapper-disabled": migrationSiDisabled,
                          })}
                        >
                          <NeoSelect
                            id="migSi"
                            value={migSI}
                            onChange={(val) => {
                              setMigSI(val);
                              setMigDirty(true);
                              dispatch(
                                setMigrationDetails({
                                  migrationProductCode: migProduct,
                                  migrationTenure: migTenure || "",
                                  migrationSI: val,
                                  migrationPremium: "",
                                })
                              );
                            }}
                            placeholder="Select Sum Insured"
                            options={migSiOptions.map((si) => ({
                              value: si.id,
                              label:
                                si.label ??
                                `₹ ${toAmount(
                                  Number(String(si.id).replace(/,/g, ""))
                                )}`,
                            }))}
                            disabled={migrationSiDisabled}
                          />
                        </div>
                      </div>

                      {/* Room Category */}
                      <div className="field-row">
                        <label htmlFor="migRoom" className="neo-label">
                          Room Category:
                        </label>
                        <div>
                          <NeoSelect
                            id="migRoom"
                            value={migRoom}
                            onChange={(val) => {
                              setMigRoom(val);
                              setMigDirty(true);
                              dispatch(
                                setMigrationDetails({
                                  migrationProductCode: migProduct,
                                  migrationTenure: migTenure || "",
                                  migrationSI: migSI || "",
                                  migrationPremium: "",
                                  migrationRoomCategory: val,
                                  migrationRoomOptions: migRoomOptions,
                                })
                              );
                            }}
                            placeholder="Select Room Category"
                            options={migRoomOptions}
                            disabled={migrationSiDisabled}
                          />
                        </div>
                      </div>

                      {/* Packages / Covers View Only */}
                      {isHealthSupercharge
                        ? (() => {
                            const optedCoverEntries = Object.entries(
                              migrationPremium?.addonCovers || {}
                            ).filter(([, cover]) => isCoverVisible(cover));
                            if (
                              Object.keys(migrationPremium?.addonCovers || {})
                                .length === 0
                            )
                              return null;
                            return (
                              <label className="theme-value packages-info">
                                Covers ({optedCoverEntries.length})
                                <span className="theme-label package-list">
                                  {optedCoverEntries
                                    .slice(0, 3)
                                    .map(([coverCode, cover]) => (
                                      <span
                                        key={coverCode}
                                        className="package-item"
                                      >
                                        <span className="package-icon">
                                          <Image
                                            src={`${IMAGE_BASE_PATH}/images/Green_ticks.svg`}
                                            alt="Checked"
                                            width={16}
                                            height={16}
                                          />
                                        </span>
                                        {cover?.name || coverCode}
                                      </span>
                                    ))}
                                  {optedCoverEntries.length > 3 && (
                                    <span className="package-more">... </span>
                                  )}
                                </span>
                                <span className="view-link">
                                  <PackageCanvasMenu
                                    toProductCode={migrationPlanToProductCode}
                                    packages={migrationPremium?.packages || {}}
                                    userCanOptOutMandatory={
                                      migrationPremium?.isMigrationPolicy
                                        ? false
                                        : mandatoryPackageOptOutFlag()
                                    }
                                    readOnly={false}
                                    addonCovers={addonCovers}
                                    optedCovers={migrationPremium?.addonCovers}
                                    onUpdate={updateMigrationPackages}
                                    packageCount={
                                      optedCoverEntries?.length || 0
                                    }
                                    subProductCode={
                                      renewalDetailsData?.subProductCode ||
                                      renewalDetailsData?.productCode
                                    }
                                  />
                                </span>
                              </label>
                            );
                          })()
                        : Object.keys(migrationPremium.packages || {}).length >
                            0 && (
                            <>
                              <label className="theme-value packages-info">
                                Packages ({migrationPremium.packageCount})
                                <span className="theme-label package-list">
                                  {getSafePackageList(migrationPremium)
                                    .slice(0, 3)
                                    .map((pkg, index) => (
                                      <span
                                        key={index}
                                        className="package-item"
                                      >
                                        <span className="package-icon">
                                          <Image
                                            src={`${IMAGE_BASE_PATH}/images/Green_ticks.svg`}
                                            alt="Checked"
                                            width={16}
                                            height={16}
                                          />
                                        </span>
                                        {pkg.replace(/_/g, " ")}
                                      </span>
                                    ))}
                                  {getSafePackageList(migrationPremium).length >
                                    3 && (
                                    <span className="package-more">... </span>
                                  )}
                                </span>
                                <span className="view-link">
                                  <PackageCanvasMenu
                                    toProductCode={migrationPlanToProductCode}
                                    packages={migrationPremium?.packages}
                                    userCanOptOutMandatory={
                                      migrationPremium?.isMigrationPolicy
                                        ? false
                                        : mandatoryPackageOptOutFlag()
                                    }
                                    readOnly={false}
                                    addonCovers={addonCovers}
                                    optedCovers={migrationPremium?.addonCovers}
                                    onUpdate={updateMigrationPackages}
                                    packageCount={
                                      migrationPremium?.packageCount
                                    }
                                    subProductCode={
                                      renewalDetailsData?.subProductCode ||
                                      renewalDetailsData?.productCode
                                    }
                                  />
                                </span>
                              </label>
                              {hasOptionalPackagesMig && (
                                <div className="optional-chip-wrapper">
                                  <div className="optional-chip">
                                    Optional Packages Available
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                    </div>

                    <div className="premium-section mt-3">
                      {!hasMigrationPremium ? (
                        <button
                          type="button"
                          className={classNames(
                            "btn rounded-pill w-75",
                            formSubmitButton
                          )}
                          onClick={handleMigrationCalculate}
                          disabled={!canCalculateMigration}
                        >
                          {migLoading ? "Calculating..." : "Calculate Premium"}
                        </button>
                      ) : (
                        <>
                          {shouldShowPremium(
                            migrationPremium,
                            "migrationPlan"
                          ) && (
                            <span className="premium-value">
                              <div className="px-1">
                                <label
                                  className={classNames(
                                    "p-0 mb-1",
                                    premiumStyle
                                  )}
                                >
                                  Premium
                                </label>
                              </div>
                              {displayPremiumAmount(
                                migrationPremium?.totalPremium,
                                migrationPremium?.tenureSI?.tenure
                              )}
                            </span>
                          )}
                          <article
                            className={classNames(
                              "detail-container mt-2",
                              mAlignCenter
                            )}
                          >
                            <div>
                              <button
                                type="button"
                                className="link-btn"
                                onClick={() =>
                                  openSelectedSchemeModal(migrationPremium)
                                }
                              >
                                View Premium Breakup
                              </button>
                            </div>
                          </article>
                        </>
                      )}
                    </div>
                  </div>

                  <div
                    className={classNames("tata-card-footer different-msg", {
                      "tata-card-selected-footer":
                        effectiveSelected === "migrationPlan",
                    })}
                  >
                    {migrationPremium?.benefits && (
                      <span className="footer-info">
                        {migrationPremium.benefits.savePercentage > 0 ? (
                          <>
                            Save {migrationPremium.benefits.savePercentage}%
                            {migrationPremium.benefits.additionalSI !== 0 &&
                            migrationPremium.packageCount > 0 ? (
                              <>
                                {" "}
                                , Additional ₹{" "}
                                {migrationPremium.benefits.additionalSI /
                                  100000}{" "}
                                L Coverage including Packages.
                              </>
                            ) : migrationPremium.benefits.additionalSI !== 0 ? (
                              <>
                                {" "}
                                & Additional ₹{" "}
                                {migrationPremium.benefits.additionalSI /
                                  100000}{" "}
                                L Coverage.
                              </>
                            ) : migrationPremium.packageCount > 0 ? (
                              <> including Packages.</>
                            ) : null}
                          </>
                        ) : migrationPremium.benefits.additionalSI !== 0 ? (
                          <>
                            Additional ₹{" "}
                            {migrationPremium.benefits.additionalSI / 100000}L
                            Coverage
                            {migrationPremium.packageCount > 0 && (
                              <> including Packages</>
                            )}
                            .
                          </>
                        ) : (
                          migrationPremium.packageCount > 0 && (
                            <>Including Packages.</>
                          )
                        )}
                      </span>
                    )}
                  </div>
                </label>
              </div>
            )}
          </div>
          <article
            className={classNames("detail-container mt-2", mAlignCenter)}
          >
            <div>
              <div className={classNames(UinBottomLine)}>
                <span className={classNames(prodNameBottomLine)}>
                  {tataProductNameMedicare}
                </span>
                <span>&nbsp;|&nbsp;</span>
                <span> {uinLabelMedicare}</span>
              </div>
            </div>
          </article>
          <div className={additionalInfoHeading}>Additional Information</div>
          <article className={classNames("detail-container mt-1 mb-2")}>
            <div>
              <div>
                <a
                  href={getCISLink(renewalDetailsData)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={policyLink}
                >
                  Customer Information Sheet
                </a>
                <span className={policyLinkDivider}>&nbsp;|&nbsp;</span>
                <a
                  href={GRIEVANCE_REDRESSAL_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={policyLink}
                >
                  Grievance Redressal
                </a>
              </div>
            </div>
          </article>

          <ul className="package-info-list d-md-none">
            {isMedicareSelectRevision && (
              <li className="package-info">
                Note: Global cover discount will be applicable if member’s
                nationality and residential status is declared.
              </li>
            )}
            {currentPlan?.packageCount > 0 && (
              <>
                <li className="package-info">
                  * Each package consists of selected add-on covers and
                  additional benefits tailored to your policy.
                </li>
                <li className="package-info">
                  {" "}
                  * Insured members applicability to packages and covers will be
                  governed by the product rules.
                </li>
              </>
            )}
          </ul>
        </div>
        <AutoDebitModal
          show={showModal}
          onClose={toggleAutoDebitModal}
          title="Auto-debit Initiated"
          message="Auto debit is initiated for your policy. Please wait for policy issuance."
          confirmText="Ok"
        />
        <RedirectModal
          show={showRedirectModal}
          onConfirm={handleRedirectModalOkay}
        />
      </>
    );
  }
);

AddOnsCard.propTypes = {
  premiumSchemes: PropTypes.array.isRequired,
  setAddOnPremium: PropTypes.func.isRequired,
  variant: PropTypes.string.isRequired,
  setAddOnScheme: PropTypes.func.isRequired,
  setSelectedPackagesArr: PropTypes.func.isRequired,
  addonCovers: PropTypes.object,
  setShowPremiumBreakupModal: PropTypes.func.isRequired,
  setPremiumDataToShow: PropTypes.func.isRequired,
  setCustomerRecommendedPlan: PropTypes.func.isRequired,
  setCustomerUpsellFlag: PropTypes.func.isRequired,
  setRemarkMigrationPolicy: PropTypes.func,
  medicareBoltRedirection: PropTypes.func,
  productName: PropTypes.string.isRequired,
  productCode: PropTypes.string.isRequired,
};

AddOnsCard.displayName = "AddOnsCard";

export default React.memo(AddOnsCard);
